import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { RealtimeChannel } from "@supabase/supabase-js";
import { toast } from "sonner";
import { getSupabaseClient } from "../utils/supabaseClient";

import { type Lead } from "@/app/(authenticated)/leads/_modules/types/get-leads-type";
import { useTwilioDevice } from "@/app/_module/hooks/useTwilioDevice";
import { hangupCall } from "@/app/_module/contexts/VoiceDeviceProvider";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import communicationService from "@/app/_module/services/communicationService";
import { dialerSessionApi } from "@/app/_module/services/dialerSessionService";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useUnifiedDialer } from "@/app/_module/contexts/UnifiedDialerContext";
import { usePredictiveDial } from "@/app/_module/contexts/PredictiveDialContext";
import { removeRealtimeChannel } from "../../../../_module/services/realtimeTeardown";
import { useRealtimeRejoin } from "@/app/_module/services/realtimeHealth";
import { dialClockSeconds, startDialClock, switchDialClock } from "../utils/dialClock";

interface CallResult {
  leadId: string;
  result: "answered" | "called" | "failed" | "skipped" | "voicemail"; // answered = customer picked up, called = no answer, failed = bad#/DNC, skipped = user skipped, voicemail = AMD detected voicemail
  disposition?: string;
  notes?: string;
  callId?: string;
  callStatus?: string;
  wasAnswered?: boolean; // Track if customer actually answered
  leadStatus?: string; // Track final lead status (Appt, Underwriting, Sold & Approved, etc.)
}

interface SessionStats {
  answered: number;
  called: number; // Total leads attempted (answered + no answer + failed + voicemail + skipped)
  totalDialTime: number; // Total dial time in seconds
  appointments: number; // Leads marked as Appt
  underwriting: number; // Leads marked as Underwriting
  sold: number; // Leads marked as Sold & Approved
  wasStopped?: boolean; // True when user manually stopped (not all leads completed)
}

interface PowerDialerSessionLogicProps {
  leads: Lead[];
  aiMatchingEnabled?: boolean;
  selectedAgentPhone?: string;
  onComplete: (stats?: SessionStats) => void;
  hasActiveNumbers?: boolean;
  autoStartFirstCall?: boolean; // Auto-dial first lead when session starts
}

interface DialerPreferences {
  autoVoicemailEnabled: boolean;
  voicemailPrompt: string;
  callTimeout: number;
  selectedVoicemailDropId?: string;
  dialAttempts: number; // 1 = no retry, 2 = one retry, 3 = two retries
}

// Helper function to validate phone numbers
function validatePhoneNumber(phone: string): boolean {
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length === 10 || cleaned.length === 11;
}

// Helper function to format phone numbers for display
function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  } else if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.slice(1, 4)}) ${cleaned.slice(4, 7)}-${cleaned.slice(7)}`;
  }
  return phone;
}

// Helper function to extract error messages
function getErrorMessage(error: any): { title: string; description: string } {
  if (error?.response?.data?.message) {
    return {
      title: "Call Failed",
      description: error.response.data.message,
    };
  }
  if (error?.message) {
    return {
      title: "Call Failed",
      description: error.message,
    };
  }
  return {
    title: "Call Failed",
    description: "An unknown error occurred",
  };
}

export function usePowerDialerSession({
  leads,
  aiMatchingEnabled,
  selectedAgentPhone,
  onComplete,
  hasActiveNumbers = true,
  autoStartFirstCall = true, // Default to auto-start for speed dialing
}: PowerDialerSessionLogicProps) {
  // Use context for currentIndex to persist across navigation
  const { currentLeadIndex, setCurrentLeadIndex, resumeOffset, dialerSource } = useUnifiedDialer();
  const currentIndex = currentLeadIndex;
  const setCurrentIndex = setCurrentLeadIndex;
  // Capture resume offset on mount so it's stable throughout the session
  const resumeOffsetRef = useRef(resumeOffset);

  const [isDialing, setIsDialing] = useState(false);

  // Bumped when the realtime channel dies so the effect re-subscribes.

  // This feature has no polling fallback — see realtimeHealth.ts.

  // Ref to track dialing state synchronously — needed because handleEndCall may fire
  // before React re-renders with isDialing=true (agent spam-clicks End instantly after Call).
  // Set synchronously in handleCall BEFORE the first await, and kept in sync via useEffect.
  const isDialingRef = useRef(false);
  const [isPaused, setIsPaused] = useState(false);
  // Ref to track paused state for synchronous access (avoids stale closure issues)
  const isPausedRef = useRef(false);
  // Ref to track if session has been stopped - prevents auto-dial after session ends
  const sessionStoppedRef = useRef(false);
  // Ref to track if the completion callback has fired — prevents double onComplete calls
  const completionFiredRef = useRef(false);
  // Ref to signal that auto-advance retry logic is pending — blocks auto-dial from racing ahead
  const pendingRetryCheckRef = useRef(false);
  // Ref + state for auto-pause after connected calls > 60s (disposition time)
  const awaitingDispositionRef = useRef(false);
  // Ref to track currentIndex for synchronous access (avoids stale closure in auto-dial timeout)
  const currentIndexRef = useRef(currentIndex);
  // Call lock to prevent multiple simultaneous call attempts
  const isCallInProgressRef = useRef(false);
  // Track the lead ID that is currently being dialed to prevent duplicate dials
  const dialingLeadIdRef = useRef<string | null>(null);
  // Storage key for persisting call results across page refresh
  const CALL_RESULTS_STORAGE_KEY = 'power-dialer-call-results';

  // Initialize call results from localStorage if available (survives page refresh)
  const [callResults, setCallResults] = useState<Record<string, CallResult>>(() => {
    if (typeof window !== 'undefined') {
      try {
        const stored = localStorage.getItem(CALL_RESULTS_STORAGE_KEY);
        if (stored) {
          return JSON.parse(stored);
        }
      } catch (e) {
        // Invalid data, use empty object
      }
    }
    return {};
  });
  // Ref to track callResults for synchronous access (avoids stale closure issues in handleStopSession)
  const callResultsRef = useRef<Record<string, CallResult>>(callResults);
  const [showDisposition, setShowDisposition] = useState(false);
  const [currentCallId, setCurrentCallId] = useState<string | null>(null);
  const [isCallAnswered, setIsCallAnswered] = useState(false);
  const [isCallRecording, setIsCallRecording] = useState(false);
  const [isDTMFListening, setIsDTMFListening] = useState(false);
  const [isSpeechProcessing, setIsSpeechProcessing] = useState(false);
  const [hasMicrophone, setHasMicrophone] = useState<boolean | null>(null);
  const [showVoicemailPrompt, setShowVoicemailPrompt] = useState(false);
  const [showVoicemailModal, setShowVoicemailModal] = useState(false);
  // Override for the badge callStatus when AMD detects voicemail
  // (Twilio callStatus only knows "in-progress", never "voicemail")
  const [callStatusOverride, setCallStatusOverride] = useState<string | null>(null);
  // Apple Screening detection — AMD detected machine_end_silence/other (could be iOS Call Screening)
  const [appleScreeningDetected, setAppleScreeningDetected] = useState(false);
  // Failure toast message — set by handleCall when a lead fails validation, consumed by UI to show toast
  const [failureToast, setFailureToast] = useState<string | null>(null);
  const [dialerPreferences, setDialerPreferences] = useState<DialerPreferences>({
    autoVoicemailEnabled: false,
    voicemailPrompt: "",
    callTimeout: 30,
    dialAttempts: 2, // Default: 2 attempts per lead (one retry) — retries voicemails and catches AMD false positives
  });
  const { user } = useUserContext();
  const lastCallTimeRef = useRef<number>(0);
  // Total dial time. Wall clock since mount also counted a panel left open: 1h 9m stored for
  // 15 minutes of dialing (Mark Gurraj, 10-19). Mode is switched in the effect below.
  const dialClockRef = useRef(startDialClock(Date.now()));
  const getDialSeconds = () => dialClockSeconds(dialClockRef.current, Date.now());

  // Backend session persistence
  const backendSessionIdRef = useRef<string | null>(null);
  const syncedCallResultsRef = useRef<Set<string>>(new Set()); // Track which call results have been synced

  // Phone number dedup: Track called phone numbers to skip duplicate numbers across leads
  const calledPhoneNumbersRef = useRef<Set<string>>(new Set());

  // Double-dial: Track per-lead retry count for immediate re-dial (power dialer only)
  const retryCountRef = useRef<Record<string, number>>({});

  // RESET THE RETRY BUDGET WHEN THE QUEUE CHANGES — i.e. on a new session.
  //
  // retryCountRef was declared once and only ever incremented; nothing cleared it. The
  // dialer panel stays mounted across sessions, so the counts survived: start a new
  // session containing a lead you already retried earlier, and `currentRetries <
  // maxRetries` was already false. The lead was silently ineligible for a redial before
  // the session even began.
  //
  // Seen on dev 2026-08-22 (Bruce Wayne, dialAttempts=3 → 2 retries):
  //   01:18:54  c029fc66  unknown/32s           retry 1 spent
  //   01:20:13  c029fc66  unknown/29s           retry 2 spent
  //   01:39:00  c029fc66  machine_end_beep/22s  budget already gone — no redial
  //
  // The voicemail was detected correctly; the dialer just thought it had already retried
  // this lead twice. Keyed on the joined id list rather than the array identity, so a
  // re-render with a fresh array does NOT hand every lead a new set of retries mid-session.
  const leadQueueKey = useMemo(() => leads.map((l) => l.id).join(","), [leads]);
  useEffect(() => {
    retryCountRef.current = {};
  }, [leadQueueKey]);
  const [isRetryAttempt, setIsRetryAttempt] = useState(false);
  // Auto-pause for disposition after connected calls > 60s
  const [awaitingDisposition, setAwaitingDisposition] = useState(false);
  // Track the last known call duration (before it resets to 0 when call ends)
  // Used by double-dial logic to skip re-dial for calls > 60s
  const lastCallDurationRef = useRef(0);

  // Keep callResultsRef in sync with state AND persist to localStorage for page refresh survival
  // Guarded rejoin: backoff + attempt cap (an unguarded retry loop hangs the tab).
  const { rejoinToken, handleStatus } = useRealtimeRejoin("power-dialer");
  useEffect(() => {
    callResultsRef.current = callResults;
    // Persist to localStorage so stats survive page refresh
    if (typeof window !== 'undefined') {
      if (Object.keys(callResults).length > 0) {
        localStorage.setItem(CALL_RESULTS_STORAGE_KEY, JSON.stringify(callResults));
      } else {
        localStorage.removeItem(CALL_RESULTS_STORAGE_KEY);
      }
    }
  }, [callResults]);

  // Keep currentIndexRef in sync for use in auto-dial timeout (avoids stale closure)
  useEffect(() => {
    currentIndexRef.current = currentIndex;
  }, [currentIndex]);

  // Spam protection: Track if we're waiting for ring before allowing navigation
  const [isWaitingForRing, setIsWaitingForRing] = useState(false);
  const navigationQueueRef = useRef<(() => void) | null>(null);
  const isNavigatingRef = useRef(false);
  const hasAutoStartedRef = useRef(false); // Track if first call was auto-started
  const lastSkipTimeRef = useRef<number>(0); // Debounce for skip/failed buttons
  const SKIP_DEBOUNCE_MS = 500; // Minimum time between skip/failed clicks

  // Check for microphone on mount
  useEffect(() => {
    const checkMicrophone = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Stop all tracks immediately after checking
        stream.getTracks().forEach(track => track.stop());
        setHasMicrophone(true);
      } catch (error) {
        setHasMicrophone(false);
        setFailureToast('No microphone detected — connect a mic and refresh');
      }
    };

    checkMicrophone();
  }, []);

  // Fetch dialer preferences on mount
  useEffect(() => {
    const fetchDialerPreferences = async () => {
      try {
        const response = await api.get({
          url: getApiUrl('/dialer-preferences'),
          auth: true,
        });

        // API returns { success, statusCode, data: preferences }
        const responseBody = response.data as any;
        if (responseBody?.data) {
          const data = responseBody.data;
          setDialerPreferences({
            autoVoicemailEnabled: data.autoVoicemailEnabled ?? false,
            voicemailPrompt: data.voicemailPrompt || "",
            callTimeout: data.callTimeout ?? 30,
            selectedVoicemailDropId: data.selectedVoicemailDropId,
            dialAttempts: data.dialAttempts ?? (data.doubleDialEnabled ? 2 : 1), // Migration fallback from old boolean
          });
        }
      } catch (error) {
        // Silently fail and use defaults - not critical for dialer function
      }
    };

    fetchDialerPreferences();
  }, []);

  // Clean stale call results from previous sessions on mount
  // Only keep results for leads in the CURRENT session — removes leftover entries
  // from prior sessions that weren't properly cleaned up (e.g., tab closed without stopping)
  // This preserves page-refresh recovery (same leads = same results kept)
  useEffect(() => {
    const currentLeadIds = new Set(leads.map(l => l.id));
    setCallResults(prev => {
      const cleaned: Record<string, CallResult> = {};
      for (const [id, result] of Object.entries(prev)) {
        if (currentLeadIds.has(id)) {
          cleaned[id] = result;
        }
      }
      callResultsRef.current = cleaned;
      return cleaned;
    });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // Create backend session on mount (fire-and-forget — dialer works without it)
  // Guard prevents duplicate sessions from React StrictMode double-mounting
  const sessionCreatedRef = useRef(false);
  useEffect(() => {
    if (sessionCreatedRef.current) return;
    if (backendSessionIdRef.current) return;
    const createBackendSession = async () => {
      sessionCreatedRef.current = true;
      try {
        const leadQueue = leads.map((l) => ({
          leadId: l.id,
          name: l.name || `${l.firstName || ''} ${l.lastName || ''}`.trim(),
          phone: l.phone || '',
          fromNumber: selectedAgentPhone,
        }));
        // Persist the source so the resume flow + session-history UI can
        // distinguish DialIQ-launched sessions from standard PowerDialer ones.
        // Read from dialerSource (set by the entry-point) rather than from
        // predictive.isPredictiveActive — the latter might not have flipped
        // true yet during the initial mount race.
        const sourceForBackend: 'manual' | 'dialiq' = dialerSource === 'dialiq' ? 'dialiq' : 'manual';
        const resp = await dialerSessionApi.createSession({ dialMode: 'power', leads: leadQueue, source: sourceForBackend });
        const sessionId = (resp.data as any)?.data?.id;
        if (sessionId) {
          backendSessionIdRef.current = sessionId;
          // A resumed or recovered run starts mid-list: save that spot now. Progress is otherwise
          // saved only with a call result, so a run that ended before its first call reopened at
          // lead 1 (Mark Gurraj 10-28). Resume sets the index ~50 ms after mount; its offset is
          // the same spot if this lands first.
          const start = Math.max(currentIndexRef.current, resumeOffsetRef.current);
          if (start > 0) {
            dialerSessionApi
              .updateProgress(sessionId, {
                currentLeadIndex: start,
                completedCalls: Object.keys(callResultsRef.current).length + resumeOffsetRef.current,
              })
              .catch(() => {});
          }
        }
      } catch (err) {
        sessionCreatedRef.current = false; // Allow retry on failure
        console.warn('[PowerDialer] Failed to create backend session:', err);
      }
    };
    if (leads.length > 0) {
      createBackendSession();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only on mount

  // Sync call results to backend (fire-and-forget — never blocks dialing)
  useEffect(() => {
    if (!backendSessionIdRef.current) return;
    const sessionId = backendSessionIdRef.current;

    const newResults = Object.entries(callResults).filter(
      ([leadId]) => !syncedCallResultsRef.current.has(leadId)
    );

    for (const [leadId, result] of newResults) {
      syncedCallResultsRef.current.add(leadId);
      const lead = leads.find((l) => l.id === leadId);
      dialerSessionApi
        .saveCallResult(sessionId, {
          leadId,
          toNumber: lead?.phone || '',
          callResult: result.result,
          disposition: result.disposition,
          notes: result.notes,
          finalLeadStatus: result.leadStatus,
          callId: result.callId,
        })
        .catch(() => {
          // Remove from synced so we can retry next time
          syncedCallResultsRef.current.delete(leadId);
        });
    }

    // Also update progress (add resumeOffset for cumulative completedCalls in resumed sessions)
    const results = Object.values(callResults);
    const offset = resumeOffsetRef.current;
    dialerSessionApi
      .updateProgress(sessionId, {
        currentLeadIndex: currentIndexRef.current,
        completedCalls: results.length + offset,
        totalAnswered: results.filter((r) => r.result === 'answered' || r.wasAnswered).length,
        totalFailed: results.filter((r) => r.result === 'failed').length,
        totalVoicemail: results.filter((r) => r.result === 'voicemail').length,
        totalSkipped: results.filter((r) => r.result === 'skipped').length,
        totalDialTime: getDialSeconds(),
      })
      .catch(() => {}); // Silently ignore
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callResults]);

  // Get singleton Supabase client (created at module level to avoid multiple GoTrueClient instances)
  const supabaseClient = getSupabaseClient();

  // Twilio Device integration
  const {
    device,
    isDeviceReady,
    isDeviceRegistering,
    activeCall,
    activeCallSid,
    connect,
    disconnect,
    mute,
    unmute,
    hold,
    unhold,
    isMuted,
    isOnHold,
    callStatus,
    callDuration,
    forwardCallsToPhone,
    microphoneAudioLevel,
    isHardwareMuted,
    audioInputDevices,
    audioOutputDevices,
    selectedInputDeviceId,
    selectedOutputDeviceId,
    setAudioInputDevice,
    setAudioOutputDevice,
    refreshAudioDevices,
    // Audio gain/volume controls
    microphoneGain,
    speakerVolume,
    setMicrophoneGain,
    setSpeakerVolume,
    // Power dialer mode tracking (prevents incoming call conflicts between leads)
    isInPowerDialerMode,
    setIsInPowerDialerMode,
    // Partial update of call metadata (for real caller ID from backend)
    updateCallMetadata,
  } = useTwilioDevice();

  // ─── DialIQ Predictive Dial integration ──────────────────────────────────
  // Only active when PowerDialerSession started a predictive session at mount
  // (which only happens when launched from DialIQ AND agency is in cohort).
  // When inactive, the calls below are safe no-ops (handled by the fallback
  // shape returned from usePredictiveDial when no provider is mounted).
  const predictive = usePredictiveDial();
  // Refs to avoid stale closures in callbacks that fire from refs/timers
  const predictiveRef = useRef(predictive);
  useEffect(() => { predictiveRef.current = predictive; }, [predictive]);

  // Tracks the active customer's Twilio CallSid in predictive mode. The agent's
  // device is always connected to the persistent conference; this ref points
  // to whichever customer was last added to that conference (so handleEndCall
  // can hang up that one leg without touching the agent's connection).
  const predictiveCustomerCallSidRef = useRef<string | null>(null);

  // Pre-bridge tracking — when handleAgentCallEnded successfully bridges a
  // pre-dialed customer into the persistent conference, the NEXT auto-dial
  // (handleCall) would normally dial that same lead again, creating a
  // duplicate outbound call to a customer who's already on the line.
  //
  // We mark the lead as pre-bridged so handleCall short-circuits its
  // dialLead step and uses the already-bridged customer's CallSid.
  const preBridgedLeadIdRef = useRef<string | null>(null);
  const preBridgedCustomerCallSidRef = useRef<string | null>(null);

  // Tracks the in-flight bridge IIFE from handleEndCall. handleCall awaits
  // this before deciding to dial so we don't race past it and create a
  // duplicate outbound call (the IIFE might take >600ms — the autoDial timer
  // could otherwise fire before the bridge completes and sets the refs).
  const pendingEndCallBridgeRef = useRef<Promise<void> | null>(null);

  // Per-customer call duration in predictive mode. The raw callDuration from
  // TwilioDeviceContext ticks for the entire session (since activeCall is the
  // persistent conference). For UI + retry-logic correctness we maintain a
  // separate counter that:
  //   - starts when the customer is connected (isCallAnswered AND we have a
  //     customer SID in predictive mode)
  //   - resets to 0 when the customer leg ends (currentCallId cleared)
  const [predictiveCallDuration, setPredictiveCallDuration] = useState(0);

  // Ref to track isInPowerDialerMode for synchronous access in timeouts/callbacks
  const isInPowerDialerModeRef = useRef(isInPowerDialerMode);
  useEffect(() => {
    isInPowerDialerModeRef.current = isInPowerDialerMode;
  }, [isInPowerDialerMode]);

  // Keep isDialingRef in sync with React state (catches all setIsDialing calls)
  useEffect(() => {
    isDialingRef.current = isDialing;
  }, [isDialing]);

  // Keep lastCallDurationRef in sync — captures duration before context resets it to 0 on disconnect
  useEffect(() => {
    if (callDuration > 0) {
      lastCallDurationRef.current = callDuration;
    }
  }, [callDuration]);

  // ─── DialIQ effective call state ─────────────────────────────────────────
  // In predictive mode, the raw activeCall represents the agent's persistent
  // conference connection — which is set the entire session. For dialer UX
  // (auto-dial gate, pause/skip buttons, in-call controls, duration), we want
  // "is there actually a customer on the line right now?". Sources of truth:
  //   - currentCallId: set to the customer's CallSid by dialLead, cleared on
  //     endCustomerLeg → tracks "are we currently dialing/talking to a customer"
  //   - isCallAnswered: flips true on the customer_answered Supabase broadcast
  //
  // We expose effective values that match standard PowerDialer semantics so
  // the rest of the code doesn't have to special-case predictive mode.
  const isPredictiveSession = predictive.isPredictiveActive && predictive.isAgentInConference;
  const effectiveActiveCall = isPredictiveSession
    ? (currentCallId ? activeCall : null)
    : activeCall;
  const effectiveCallDuration = isPredictiveSession ? predictiveCallDuration : callDuration;

  // Predictive keeps dialing for the agent while they wait in the conference, so that counts too.
  const dialClockBusy = isDialing || !!effectiveActiveCall || isPredictiveSession;
  useEffect(() => {
    dialClockRef.current = switchDialClock(
      dialClockRef.current,
      isPaused ? "paused" : dialClockBusy ? "busy" : "idle",
      Date.now(),
    );
  }, [isPaused, dialClockBusy]);

  // Per-customer duration timer in predictive mode.
  useEffect(() => {
    if (!isPredictiveSession) {
      // Standard mode — TwilioDeviceContext owns the timer.
      return;
    }
    if (!currentCallId) {
      // No customer on the line — reset.
      setPredictiveCallDuration(0);
      return;
    }
    if (!isCallAnswered) {
      // Customer dialed but hasn't picked up yet — don't start counting.
      return;
    }
    // Customer connected — start ticking.
    const interval = setInterval(() => {
      setPredictiveCallDuration((d) => d + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [isPredictiveSession, currentCallId, isCallAnswered]);

  // Keep lastCallDurationRef in sync with the EFFECTIVE duration so retry
  // logic uses per-customer time, not session time, in predictive mode.
  useEffect(() => {
    if (isPredictiveSession && effectiveCallDuration > 0) {
      lastCallDurationRef.current = effectiveCallDuration;
    }
  }, [isPredictiveSession, effectiveCallDuration]);

  // ─── Predictive pre-dial trigger ─────────────────────────────────────────
  // Each second the call is active, give PredictiveDialContext a chance to
  // pre-dial the NEXT lead. The context's own internal rate-limit ensures we
  // only hit the backend at most every 3 seconds, so this is cheap.
  //
  // The context decides on its own when to actually trigger — based on the
  // agent's stats baseline (e.g. trigger ~3min into a 4min-avg-talk session).
  useEffect(() => {
    if (!predictive.isPredictiveActive) return;
    if (!isCallAnswered) return;
    if (callDuration <= 0) return;

    // Build a getNextLead closure that returns the lead AFTER the current one
    // in the queue (only if it has a phone number — predictive can't dial null).
    const getNextLead = (): Lead | null => {
      const nextIdx = currentIndexRef.current + 1;
      const next = leads[nextIdx];
      if (!next || !next.phone) return null;
      return next;
    };

    const fromNumber = selectedAgentPhone || '';
    if (!fromNumber) return; // Need a caller-ID to pre-dial

    // Fire-and-forget — predictive.notifyCallElapsed has its own rate limit
    predictive.notifyCallElapsed(callDuration * 1000, getNextLead, fromNumber)
      .catch(() => { /* silently fall back */ });
  }, [callDuration, isCallAnswered, leads, selectedAgentPhone, predictive]);

  const currentLead = leads[currentIndex];
  const [formData, setFormData] = useState<Lead>(currentLead || ({} as Lead));

  // Update formData when current lead changes (by ID, not object reference)
  // CRITICAL FIX: Using currentLead?.id instead of currentLead as dep prevents false triggers
  // when the leads array is recreated (React Query refetch, parent re-render) — which would
  // clear currentCallId mid-call and break auto-advance (the exact bug Brandon reported).
  useEffect(() => {
    if (currentLead) {
      setFormData(currentLead);
      setIsCallAnswered(false); // Reset call answered state for new lead
      setCurrentCallId(null);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally keyed on lead ID, not object reference
  }, [currentLead?.id]);

  // Keep formData in sync when lead object reference updates (e.g., React Query refetch)
  // This does NOT reset callId or callAnswered — those only reset on actual lead change above
  useEffect(() => {
    if (currentLead) {
      setFormData(currentLead);
    }
  }, [currentLead]);

  // Auto-start first call when session begins (speed dial mode)
  // This runs once when the device is ready and we have a first lead.
  //
  // Uses effectiveActiveCall so predictive mode correctly auto-dials: in
  // predictive mode raw activeCall is the persistent conference (always set)
  // — we need to treat "no customer yet" as the same as "no call" for the
  // gate to pass.
  useEffect(() => {
    if (
      autoStartFirstCall &&
      !hasAutoStartedRef.current &&
      currentIndex === 0 &&
      currentLead &&
      isDeviceReady &&
      hasMicrophone !== false &&
      hasActiveNumbers &&
      !isDialing &&
      !effectiveActiveCall
    ) {
      hasAutoStartedRef.current = true;
      // Protect against incoming calls for the duration of the power dialer session
      setIsInPowerDialerMode(true);

      // Trigger the first call
      const callTimer = setTimeout(() => {
        // Double-check conditions before calling (including sessionStoppedRef and device ready for safety)
        if (!isDialing && !effectiveActiveCall && !isPaused && !sessionStoppedRef.current && isDeviceReady) {
          handleCall();
        }
      }, 500);

      return () => {
        clearTimeout(callTimer);
      };
    }
  }, [autoStartFirstCall, currentIndex, currentLead, isDeviceReady, hasMicrophone, hasActiveNumbers, isDialing, effectiveActiveCall, isPaused]);

  // Resume auto-dial when device recovers from reconnect during active session
  // If the device went offline (isDeviceReady false→true) and we're mid-session with no active call,
  // the normal auto-dial effect won't re-fire because currentIndex hasn't changed.
  // This effect sets a flag that the main auto-dial effect picks up on next render.
  const prevDeviceReadyRef = useRef(isDeviceReady);
  const shouldResumeAfterReconnectRef = useRef(false);
  useEffect(() => {
    const wasNotReady = !prevDeviceReadyRef.current;
    prevDeviceReadyRef.current = isDeviceReady;

    // Device just became ready again (false → true) — flag for resume
    if (wasNotReady && isDeviceReady && !sessionStoppedRef.current) {
      shouldResumeAfterReconnectRef.current = true;
    }
  }, [isDeviceReady]);

  // Sync callId from activeCallSid (for all dialer types)
  // activeCallSid is updated separately when the call is accepted, bypassing React's reference detection issue
  //
  // PREDICTIVE MODE: this effect is SKIPPED — activeCallSid would be the
  // persistent conference SID, same value for every customer in the session.
  // currentCallId is set directly to the customer's CallSid in handleCall
  // (right after predictive.dialLead returns).
  useEffect(() => {
    if (predictive.isPredictiveActive && predictive.isAgentInConference) {
      // Predictive owns currentCallId via the customer's CallSid set in handleCall.
      return;
    }
    if (activeCallSid && activeCallSid !== currentCallId) {
      setCurrentCallId(activeCallSid);

      // Update call results with the actual call ID
      if (currentLead) {
        setCallResults((prev) => ({
          ...prev,
          [currentLead.id]: {
            ...prev[currentLead.id],
            callId: activeCallSid,
          },
        }));
      }
    }
  }, [activeCallSid, currentCallId, currentLead, predictive.isPredictiveActive, predictive.isAgentInConference]);

  // Listen to call status changes from Twilio Device
  useEffect(() => {
    // CRITICAL: When we have an active call, stop showing "Calling..." state
    // This enables the action buttons (Skip, Bad Number, DNC, etc.)
    if (activeCall) {
      if (isDialing) {
        setIsDialing(false);
      }
    } else {
      // No active call - only reset isCallAnswered when call ends (not during initial dialing)
      // Don't reset isDialing here - let the safety timeout handle stuck states
      setIsCallAnswered(false);
      // Release call lock when call ends so next call can proceed
      isCallInProgressRef.current = false;
    }

    // Update call status in results
    if (currentLead && callStatus) {
      setCallResults((prev) => ({
        ...prev,
        [currentLead.id]: {
          ...prev[currentLead.id],
          callStatus: callStatus,
        },
      }));

      // NOTE: We NO LONGER check for 'in-progress' to mark as answered here
      // The 'in-progress' status fires when the AGENT joins the conference, not when the customer answers
      // Instead, we listen for the 'customer_answered' Supabase realtime event below
    }
  }, [activeCall, callStatus, currentLead, isCallAnswered, isDialing]);

  // Safety timeout: Reset isDialing after 10 seconds if call never connects
  // This prevents buttons from being permanently locked
  useEffect(() => {
    if (isDialing && !activeCall) {
      const timeout = setTimeout(() => {
        setIsDialing(false);
      }, 10000); // 10 second timeout - reduced from 30s for better UX

      return () => clearTimeout(timeout);
    }
  }, [isDialing, activeCall]);

  // SAFETY NET: If agent has been in an active call for longer than callTimeout + 15s
  // without the call being answered (no customer in conference), disconnect.
  // This catches cases where `customer_disconnected` broadcast was missed entirely
  // (e.g., Supabase outage, network issue). Without this, the agent sits in an empty
  // conference forever until they manually Pause→Resume.
  // It is also the ring limit: the lead's leg has no Twilio timeout (60 s default). At +5 it hung
  // up on voicemails that answer 34-39 s after the dial (Nikk Gjurashaj, 2026-11-02).
  useEffect(() => {
    if (activeCall && !isCallAnswered && !isDialing && isInPowerDialerModeRef.current) {
      const safetyTimeout = (dialerPreferencesRef.current.callTimeout + 15) * 1000;
      const timer = setTimeout(() => {
        // Re-check conditions — call might have been answered or ended during the wait
        if (!isCallAnsweredRef.current && !isPausedRef.current && isInPowerDialerModeRef.current) {
          console.warn('[PowerDialer] Safety timeout: call unanswered for', safetyTimeout / 1000, 's — disconnecting stuck conference');
          setFailureToast('Call timed out — no answer');
          disconnectRef.current();
        }
      }, safetyTimeout);

      return () => clearTimeout(timer);
    }
  }, [activeCall, isCallAnswered, isDialing]);

  // Refs to hold current values for Supabase callbacks (avoids stale closure issues)
  const activeCallSidRef = useRef<string | null>(null);
  const isCallAnsweredRef = useRef(false);
  const currentLeadRef = useRef<Lead | null>(null);
  const dialerPreferencesRef = useRef(dialerPreferences);

  // CRITICAL FIX: Ref for disconnect function to stabilize Supabase channel subscription.
  // Without this, `disconnect` changes identity on every re-render (callDuration increments
  // every second in TwilioDeviceContext), causing the Supabase channel to unsubscribe/re-subscribe
  // every second. This creates gaps where `customer_disconnected` events are missed.
  // For no-answer calls, `customer_disconnected` is the ONLY way to end the agent's conference
  // leg — missing it leaves the agent stuck in silence (the "weird noise" bug).
  const disconnectRef = useRef(disconnect);
  useEffect(() => {
    disconnectRef.current = disconnect;
  }, [disconnect]);

  // CRITICAL FIX: Separate ref to track which lead is CURRENTLY being called/connected
  // This is different from currentLeadRef which tracks UI display and may advance to the NEXT lead
  // before the current call completes. leadBeingCalledRef only updates when a call actually starts.
  const leadBeingCalledRef = useRef<Lead | null>(null);

  // Track the last call SID even after it ends — used to accept late-arriving AMD voicemail
  // broadcasts that fire after the call/conference has already ended.
  const lastCallSidRef = useRef<string | null>(null);

  // Keep refs in sync with state
  useEffect(() => {
    // Track the last known call SID so late-arriving AMD broadcasts can still match
    if (activeCallSid) {
      lastCallSidRef.current = activeCallSid;
    }
    activeCallSidRef.current = activeCallSid;
  }, [activeCallSid]);

  useEffect(() => {
    isCallAnsweredRef.current = isCallAnswered;
  }, [isCallAnswered]);

  useEffect(() => {
    currentLeadRef.current = currentLead || null;
  }, [currentLead]);

  useEffect(() => {
    dialerPreferencesRef.current = dialerPreferences;
  }, [dialerPreferences]);

  // Subscribe to Supabase realtime for customer_answered and customer_disconnected events
  // customer_answered fires when the customer actually answers the call (joins the conference)
  // customer_disconnected fires when the customer hangs up or the call ends
  // NOTE: We use refs in callbacks to always get the latest values and avoid stale closures
  useEffect(() => {
    if (!user?.user?.id || !supabaseClient) {
      return;
    }

    // Use unique channel name with :powerdialer suffix to avoid conflicts with IncomingCallContext
    // which also subscribes to incoming_calls:{userId}. Duplicate subscriptions cause channel to close.
    const channelName = `incoming_calls:${user.user.id}:powerdialer`;

    const channel = supabaseClient.channel(channelName);

    channel.on('broadcast', { event: 'customer_answered' }, (payload) => {
      const currentActiveCallSid = activeCallSidRef.current;
      const currentIsCallAnswered = isCallAnsweredRef.current;
      // CRITICAL FIX: Use leadBeingCalledRef instead of currentLeadRef
      // currentLeadRef may have already advanced to the NEXT lead before the current call completes
      // leadBeingCalledRef tracks the lead that is ACTUALLY being called right now
      const leadBeingCalled = leadBeingCalledRef.current;

      // Check if this event is for our current call (using refs for latest values)
      if (payload?.payload?.parentCallSid === currentActiveCallSid && !currentIsCallAnswered && leadBeingCalled) {
        // CRITICAL: Update refs SYNCHRONOUSLY before state updates
        // This ensures auto-advance and completion effects see the correct values
        // even if customer disconnects immediately after answering (before React batches state updates)
        isCallAnsweredRef.current = true;

        // Also update callResultsRef synchronously so completion stats are accurate
        const existing = callResultsRef.current[leadBeingCalled.id];
        if (existing?.result !== "skipped" && existing?.result !== "failed" && existing?.result !== "answered" && existing?.result !== "voicemail") {
          callResultsRef.current = {
            ...callResultsRef.current,
            [leadBeingCalled.id]: {
              ...existing,
              leadId: existing?.leadId || leadBeingCalled.id,
              result: "answered" as const,
              wasAnswered: true,
            },
          };
        }

        // Update outbound caller ID with the REAL number the backend used
        // This replaces the displayFrom estimate with the actual caller ID
        // (important when AI matching picks a different number than selectedAgentPhone)
        if (payload?.payload?.callerId) {
          updateCallMetadata({ outboundCallerId: payload.payload.callerId });
        }

        // Now update state for UI reactivity
        setIsCallAnswered(true);
        // Only clear voicemail badge if AMD hasn't already detected a machine.
        // Race condition: customer_answered fires when voicemail machine picks up (it "answered"),
        // but machine_start may have already set callStatusOverride to 'voicemail'.
        // If AMD confirmed human later, the customer_answered event for THAT will clear it.
        setCallStatusOverride(prev => prev === 'voicemail' ? prev : null);
        setAppleScreeningDetected(prev => prev ? prev : false);
      }
    });

    // Listen for customer_disconnected event - fires when customer hangs up or call ends
    channel.on('broadcast', { event: 'customer_disconnected' }, (payload) => {
      const currentActiveCallSid = activeCallSidRef.current;
      const payloadCallSid = payload?.payload?.parentCallSid;

      // Match against current call OR the last recently-ended call.
      // Late-arriving AMD voicemail broadcasts (machine_end_beep at ~10s) can fire AFTER the
      // conference already ended (voicemail machine hung up). In that case activeCallSidRef
      // is null, but lastCallSidRef still holds the SID. We accept voicemail broadcasts for
      // recently-ended calls so the retry logic sees result="voicemail" instead of "answered".
      const isCurrentCall = payloadCallSid === currentActiveCallSid;
      const isRecentlyEndedCall = !currentActiveCallSid && payloadCallSid === lastCallSidRef.current;

      if (isCurrentCall || isRecentlyEndedCall) {
        const callStatus = payload?.payload?.callStatus;

        // AMD VOICEMAIL: DON'T disconnect the call.
        // AMD false positives kill live human calls — the agent hears the person talking
        // then BOOM call drops. Instead, just mark as voicemail and let the call continue.
        // If it's REAL voicemail: the machine hangs up naturally → conference ends → auto-advance → retry.
        // If AMD was WRONG (false positive): agent keeps talking to the human uninterrupted.
        // Agent can also press Skip or End Call to move on manually.
        if (callStatus === 'voicemail') {
          // Show "Voicemail" in the badge so the agent knows AMD detected a machine.
          // The call stays connected (no disconnect) — if AMD was wrong and it's a real
          // human, the agent is still talking and can ignore the badge.
          // Apple Screening (iOS 26+): machine_end_silence/other = could be Apple screening
          // machine_end_beep = confirmed voicemail (Apple screening does NOT beep)
          setAppleScreeningDetected(!!payload?.payload?.appleScreeningPossible);
          setCallStatusOverride('voicemail');
          const voicemailLead = currentLeadRef.current;
          if (voicemailLead) {
            const leadId = voicemailLead.id;
            callResultsRef.current = {
              ...callResultsRef.current,
              [leadId]: {
                ...callResultsRef.current[leadId],
                leadId,
                result: "voicemail" as const,
                wasAnswered: false,
              },
            };
            setCallResults(prev => ({
              ...prev,
              [leadId]: {
                ...prev[leadId],
                leadId,
                result: "voicemail" as const,
                wasAnswered: false,
              },
            }));
          }

          // LATE BROADCAST: If the call already ended (isRecentlyEndedCall), the call is over.
          // Just update the result to "voicemail" for retry — don't try to disconnect.
          if (isRecentlyEndedCall) {
            return;
          }

          // Power dialer auto-skip: disconnect and auto-advance to next lead.
          // On power dialer, the agent is already listening — they can hear if it's
          // a real person or voicemail. So we only skip disconnect when:
          // - earlyDetection: machine_start with voicemail drop ON → wait for beep
          // - voicemailDropPending: voicemail drop about to play → conference will end naturally
          // NOTE: appleScreeningPossible is NOT checked for power dialer — that guard is for
          // multi-line stealth AMD where the agent can't hear anything. On power dialer,
          // the agent can hear Apple screening and decide themselves.
          const isEarlyDetection = !!payload?.payload?.earlyDetection;
          const isDropPending = !!payload?.payload?.voicemailDropPending;

          // Check if this is a CONFIRMED voicemail (beep detected) — safe to auto-disconnect
          const isConfirmedVoicemail = !!payload?.payload?.confirmedVoicemail;

          if (isConfirmedVoicemail) {
            // Beep confirmed — this IS a voicemail. Auto-disconnect after brief delay
            // so the "Voicemail" badge renders before the call ends.
            console.log('[PowerDialer] Confirmed voicemail (beep detected) — auto-disconnecting');
            if (isInPowerDialerModeRef.current) {
              setTimeout(() => {
                leadBeingCalledRef.current = null;
                disconnectRef.current();
              }, 300);
              return;
            }
          }

          // SAFETY: Don't auto-disconnect on EARLY detection (machine_start with earlyDetection=true)
          // if the call has been active long enough that a real human could be talking.
          // This ONLY applies to earlyDetection broadcasts — post-grace broadcasts (earlyDetection=false)
          // have already waited through the grace period and confirmed it's still machine.
          const currentCallDuration = lastCallDurationRef.current || 0;
          if (isEarlyDetection && currentCallDuration > 8 && !isConfirmedVoicemail) {
            console.log(`[PowerDialer] Early AMD detection but call active ${currentCallDuration}s — skipping auto-disconnect (agent can hear the call)`);
            return;
          }

          if (isInPowerDialerModeRef.current && !isEarlyDetection && !isDropPending) {
            setTimeout(() => {
              leadBeingCalledRef.current = null;
              disconnectRef.current();
            }, 300);
            return;
          }

          return; // DON'T disconnect — early detection, voicemail drop pending, or manual dialing
        }

        // REAL disconnect: customer actually hung up (not from AMD)
        // Conference ends naturally when customer leaves (endConferenceOnExit: true)
        // Skip if this is a late broadcast for an already-ended call — nothing to disconnect.
        if (!isRecentlyEndedCall) {
          leadBeingCalledRef.current = null;
          disconnectRef.current();
        }
      }
    });

    channel.subscribe(handleStatus);

    return () => {
      removeRealtimeChannel(channel);
    };
  }, [user?.user?.id, supabaseClient, rejoinToken]); // CRITICAL FIX: Removed `disconnect` from deps — use disconnectRef instead
  // `disconnect` changes identity on every re-render (TwilioDeviceContext recreates it each render),
  // which caused this Supabase channel to unsubscribe/re-subscribe every second during calls.
  // This created gaps where customer_disconnected events were missed, leaving agents stuck
  // in empty conferences after no-answer calls (the Pause→Resume-on-every-call bug).

  // Update callResults immediately when customer answers (so answeredCount shows 1 during the call)
  useEffect(() => {
    if (isCallAnswered && currentLead) {
      setCallResults((prev) => {
        const existing = prev[currentLead.id];
        // Don't overwrite if already has a manual result (skipped, failed, voicemail) or already marked as answered
        if (existing?.result === "skipped" || existing?.result === "failed" || existing?.result === "answered" || existing?.result === "voicemail") {
          return prev;
        }
        const updated: Record<string, CallResult> = {
          ...prev,
          [currentLead.id]: {
            ...existing,
            leadId: currentLead.id,
            result: "answered" as const,
            wasAnswered: true,
          },
        };
        // Also update the ref immediately to avoid stale closure issues when session ends
        callResultsRef.current = updated;
        return updated;
      });
    }
  }, [isCallAnswered, currentLead]);

  // Reset DTMF listening when call ends
  // NOTE: We NO LONGER auto-enable isDTMFListening when call connects
  // The "Listening" badge should only show when user presses Collect SSN/DL/Other buttons
  // The InsuraABotKeyGrabber component will set isDTMFListening=true when buttons are pressed
  useEffect(() => {
    if (!activeCall) {
      setIsDTMFListening(false);
    }
  }, [activeCall]);

  // Cleanup on call end
  // NOTE: Don't clear currentCallId here - let the auto-advance effect use it first
  // The auto-advance effect checks (!activeCall && currentCallId) to trigger advancement
  useEffect(() => {
    if (!activeCall) {
      // setCurrentCallId(null); - Moved to auto-advance effect after it processes
      setIsCallRecording(false);
      setIsDTMFListening(false);
      // CRITICAL FIX: Clear leadBeingCalledRef when call ends
      // This ensures the next call will set the correct lead
      leadBeingCalledRef.current = null;
      // Also reset the call lock so a new call can be initiated
      isCallInProgressRef.current = false;
      // NOTE: Do NOT clear dialingLeadIdRef here - it will be cleared when we advance to the next lead
      // Clearing it here causes a race condition where the same lead can be dialed twice
      // before the auto-advance timeout fires to move to the next index
    }
  }, [activeCall]);

  // Track if we should auto-advance after call ends
  const shouldAutoAdvanceRef = useRef(false);
  // Track if manual action (skip/failed) already advanced - prevent auto-advance from double-advancing
  const manuallyAdvancedRef = useRef(false);
  // Track if a call was attempted for the current lead — used to detect calls that
  // were rejected by carrier BEFORE Twilio's 'accept' event (so currentCallId is never set).
  // Without this, auto-advance condition (!activeCall && currentCallId) never triggers.
  const callAttemptedForLeadRef = useRef<string | null>(null);
  // Timestamp when pendingRetryCheckRef was set to true — used by safety timeout to detect
  // when the ref has been stuck for too long (e.g., React cleanup cleared the retry timer).
  const pendingRetryCheckSetAtRef = useRef<number | null>(null);

  // Auto-advance to next lead when call ends - mark based on whether customer answered
  useEffect(() => {
    // If we had an active call and now it's gone
    // BUG FIX: Also trigger when callAttemptedForLeadRef is set but currentCallId is null.
    // This happens when a call is rejected by the carrier BEFORE Twilio's 'accept' event fires
    // (e.g., busy, invalid number, instant no-answer). Without this, the dialer silently stalls
    // because the auto-advance condition (!activeCall && currentCallId) is never satisfied.
    const callEndedWithoutAccept = !activeCall && !currentCallId && callAttemptedForLeadRef.current;
    if (!activeCall && (currentCallId || callEndedWithoutAccept)) {
      // Clear the call-attempted flag now that we're processing
      callAttemptedForLeadRef.current = null;

      // Skip auto-advance if manual action (skip/failed) already handled it
      if (manuallyAdvancedRef.current) {
        manuallyAdvancedRef.current = false;
        pendingRetryCheckRef.current = false; pendingRetryCheckSetAtRef.current = null; // Manual advance — no retry pending
        setCurrentCallId(null); // Clear call ID after manual advance
        return;
      }

      // NOTE: Don't set sessionStoppedRef here. Setting it prematurely blocks auto-dial
      // and resume from working. advanceToNextLead handles it when actually reaching the end.

      shouldAutoAdvanceRef.current = true;
      pendingRetryCheckRef.current = true; // Block auto-dial until retry decision is made
      pendingRetryCheckSetAtRef.current = Date.now(); // Track when it was set for safety timeout

      // AMD TIMING: Use a longer delay for short "answered" calls to wait for AMD results.
      // When a voicemail machine picks up, the call shows "answered" but AMD hasn't reported yet.
      // machine_end_beep fires at ~10-15s. If the VM machine hangs up early (e.g., at 12s)
      // the conference ends but AMD hasn't sent machine_end_beep yet. A 500ms delay isn't
      // enough — the AMD broadcast arrives 1-3s later. Use 2500ms for short answered calls
      // to give AMD time to report. For long calls (>30s) or non-answered, use fast 500ms.
      const existingResult = callResultsRef.current[currentLead?.id || ''];
      const isShortAnsweredCall = existingResult?.result === 'answered' && lastCallDurationRef.current < 30;
      const autoAdvanceDelay = isShortAnsweredCall ? 2500 : 500;

      // Brief moment before advancing to next lead
      const timer = setTimeout(() => {
        // Also check isInPowerDialerModeRef — if cleared externally (e.g., agent started a manual call
        // from a lead page), the power dialer should NOT auto-advance to the next lead.
        //
        // CALL-WAITING GUARD: if the agent is mid hold/merge handoff, the
        // SDK disconnecting from the old conference briefly looks like a
        // "call ended" to the auto-advance logic — but we ABSOLUTELY must
        // not re-dial the current lead (they're on hold music) or advance
        // to the next lead. The incoming call flow owns the lifecycle until
        // agent ends everything and manually hits Resume.
        const callWaitingActive = typeof window !== 'undefined'
          && sessionStorage.getItem('__callwaiting_active') === '1';
        if (currentLead && !isPausedRef.current && !sessionStoppedRef.current && shouldAutoAdvanceRef.current && !manuallyAdvancedRef.current && isInPowerDialerModeRef.current && !callWaitingActive) {
          shouldAutoAdvanceRef.current = false;

          // Check if customer was already marked as answered in callResultsRef
          // This is more reliable than isCallAnsweredRef because the customer_answered callback
          // updates callResultsRef synchronously, and it won't be reset by the activeCall effect
          const existing = callResultsRef.current[currentLead.id];

          // Don't overwrite if already marked as answered, skipped, failed, or voicemail
          if (existing?.result !== "skipped" && existing?.result !== "failed" && existing?.result !== "answered" && existing?.result !== "voicemail") {
            // Only update if not already answered - this handles the "called" (no answer) case
            const updated: Record<string, CallResult> = {
              ...callResultsRef.current,
              [currentLead.id]: {
                ...existing,
                leadId: existing?.leadId || currentLead.id,
                result: "called" as const,
                wasAnswered: false,
              },
            };
            callResultsRef.current = updated;

          }

          // Now update the state (for UI reactivity)
          setCallResults((prev) => {
            const existingState = prev[currentLead.id];
            // Don't overwrite if already has a manual result (skipped, failed, voicemail) or already answered
            if (existingState?.result === "skipped" || existingState?.result === "failed" || existingState?.result === "answered" || existingState?.result === "voicemail") {
              return prev;
            }
            return {
              ...prev,
              [currentLead.id]: {
                ...existingState,
                leadId: existingState?.leadId || currentLead.id,
                result: "called" as const,
                wasAnswered: false,
              },
            };
          });

          // Clear call ID now that we've processed it
          setCurrentCallId(null);

          // Dial attempts: Retry the same lead up to (dialAttempts - 1) times
          // dialAttempts=1 → no retry, dialAttempts=2 → one retry, dialAttempts=3 → two retries
          // SKIP retry if the call was answered, skipped, or lasted > 60 seconds
          // Voicemail is OK to redial (person didn't actually answer)
          const currentResult = callResultsRef.current[currentLead.id]?.result;
          // Voicemail machines "answer" the call (wasAnswered=true) but aren't real conversations.
          // If the result was explicitly marked as voicemail (by AMD), treat as unanswered.
          //
          // ⚠️ THE DURATION FLOOR IS WHAT MAKES RETRIES ACTUALLY HAPPEN — do not drop it.
          //
          // "Answered" here means Twilio connected the call, nothing more. Relying on AMD to
          // separate voicemail from a person does not work in practice: measured across every
          // power dial on 2026-08-21, AMD returned a machine verdict on ~1% of calls —
          // unknown 2,502, null 2,290, human 316, machine_* 67. So a voicemail pickup is almost
          // never labelled voicemail; it just looks answered, and the redial was suppressed.
          //
          // The effect on a real agent (Mark Gurraj, reported 2026-08-21): with dialAttempts=2,
          // busy/no_answer/failed retried 100% of the time, but `completed` calls retried on
          // 1 of 35 — and those averaged FOURTEEN SECONDS. He set 1 retry, then 2, and saw no
          // second dial, because a 3-second pickup counted as a conversation.
          //
          // A call has to last at least this long before we accept it as a real answer. Below
          // it, the lead is redialed like any other unanswered attempt.
          //
          // 10s is picked from the data, not taste. Replaying every completed power dial since
          // 2026-08-21 against candidate thresholds:
          //
          //     cutoff   real people redialed   voicemails/dead air redialed
          //       8s              17                     1,515
          //      10s              37                     1,824   <-- best value
          //      15s              95                     2,144
          //      20s             120                     2,301
          //
          // Raising 10s -> 20s wins 477 more correct redials but costs 83 more redials of
          // someone who actually picked up. The duration histogram says why: sub-10s is 1,824
          // calls and almost none of them are people (0-5s runs 857 non-human to 1 human),
          // whereas humans bunch up in the 10-15s band. So 10s takes nearly all of the upside
          // and leaves the risky band alone.
          //
          // Note we cannot simply ask AMD whether it heard a person: the client is only sent
          // MACHINE events (machine_start / machine_end_*), never a human verdict, and AMD
          // classifies ~1% of calls anyway. Duration is the only signal available here.
          const MIN_REAL_ANSWER_SECONDS = 10;
          const wasCallAnswered =
            (currentResult === "answered" || callResultsRef.current[currentLead.id]?.wasAnswered) &&
            currentResult !== "voicemail" &&
            lastCallDurationRef.current >= MIN_REAL_ANSWER_SECONDS;
          const wasCallLong = lastCallDurationRef.current > 60; // > 1 minute = real conversation
          const maxRetries = dialerPreferencesRef.current.dialAttempts - 1; // 1→0, 2→1, 3→2
          const currentRetries = retryCountRef.current[currentLead.id] || 0;
          if (
            maxRetries > 0 &&
            currentRetries < maxRetries &&
            !wasCallAnswered &&
            !wasCallLong &&
            currentResult !== "skipped"
          ) {
            retryCountRef.current[currentLead.id] = currentRetries + 1;
            setIsRetryAttempt(true);
            // Reset the voicemail/called result so the retry call starts fresh.
            // Without this, customer_answered guards see stale "voicemail" and refuse to
            // update the result to "answered", causing the call to be treated as voicemail
            // and disconnected immediately when the customer actually picks up.
            // NOTE: Only update the ref, NOT the state. Calling setCallResults triggers a
            // re-render which can cause the auto-dial effect to re-fire and race ahead.
            // The retry logic and customer_answered handler both read from the ref.
            callResultsRef.current = {
              ...callResultsRef.current,
              [currentLead.id]: {
                ...callResultsRef.current[currentLead.id],
                result: "called" as const,
                wasAnswered: false,
              },
            };
            // Reset locks to allow re-dialing the same lead
            dialingLeadIdRef.current = null;
            isCallInProgressRef.current = false;
            lastCallTimeRef.current = 0; // Reset debounce
            // CRITICAL: Un-stop session for the retry — sessionStoppedRef may have been set
            // at line 564 for the last lead. We need to allow the retry call to go through.
            // It will be re-set after the retry call ends and we actually advance past the last lead.
            sessionStoppedRef.current = false;
            // Re-dial after brief pause
            setTimeout(() => {
              setIsRetryAttempt(false);
              pendingRetryCheckRef.current = false; pendingRetryCheckSetAtRef.current = null; // Retry decided — unblock auto-dial
              // Also check isInPowerDialerModeRef — if cleared externally (agent started manual call),
              // don't retry as it would conflict with the manual call.
              // Call-waiting guard — never retry while call-waiting handoff
              // is mid-flight. The current lead is held with hold music; a
              // retry would create a SECOND call to the same person.
              const callWaitingActive3 = typeof window !== 'undefined'
                && sessionStorage.getItem('__callwaiting_active') === '1';
              if (!isPausedRef.current && !sessionStoppedRef.current && isDeviceReady && isInPowerDialerModeRef.current && !callWaitingActive3) {
                handleCall();
              } else if (!isPausedRef.current && !sessionStoppedRef.current) {
                // BUG FIX: Retry conditions not met (device not ready, mode changed) but session
                // should continue. Without this fallback, the dialer enters a dead state — no retry
                // happened and no advancement happened, so nothing triggers the next call.
                console.warn('[PowerDialer] Retry conditions not met — advancing to next lead instead');
                lastCallTimeRef.current = 0;
                dialingLeadIdRef.current = null;
                isCallInProgressRef.current = false;
                advanceToNextLead(true);
              }
            }, 1500); // 1.5s pause before retry
            return; // Don't advance to next lead
          }

          // No retry needed — unblock auto-dial for next lead
          pendingRetryCheckRef.current = false; pendingRetryCheckSetAtRef.current = null;
          // CRITICAL: Reset debounce so the next lead can dial immediately.
          // Without this, the 3s debounce from the last retry call blocks auto-dial
          // for the next lead (total time from retry start to advance < 3s).
          lastCallTimeRef.current = 0;

          // AUTO-PAUSE FOR DISPOSITION: If the call was a real conversation (answered + over 60s),
          // pause the dialer so the agent has time to take notes, update lead status, etc.
          // The dialer does NOT auto-advance — the agent clicks Skip/Next when ready.
          // Short calls, voicemails, no-answers, and carrier rejections still auto-advance immediately.
          if (wasCallAnswered && wasCallLong) {
            awaitingDispositionRef.current = true;
            setAwaitingDisposition(true);
            setCurrentCallId(null);
            return; // Don't advance — wait for agent to click Skip/Next when done with notes
          }

          // Calculate next index (use ref to avoid stale closure inside setTimeout)
          const nextIndex = currentIndexRef.current + 1;

          // CRITICAL: If this is the last lead, mark session as stopped BEFORE incrementing
          // This prevents the auto-dial effect from firing during the race window
          if (nextIndex >= leads.length) {
            sessionStoppedRef.current = true;
          }

          // SAFEGUARD: Verify the next lead exists and get it before advancing
          const nextLead = leads[nextIndex];
          if (nextIndex < leads.length && !nextLead) {
            console.error('[PowerDialer] Next lead is undefined at index', nextIndex, 'leads.length:', leads.length);
            sessionStoppedRef.current = true;
            return;
          }

          // Advance index and trigger auto-dial via state change
          if (!isNavigatingRef.current) {
            isNavigatingRef.current = true;
            // CRITICAL: Update BOTH refs SYNCHRONOUSLY before state update to prevent stale closure race
            currentIndexRef.current = nextIndex;
            // Also update currentLeadRef to ensure handleCall gets the correct lead
            currentLeadRef.current = nextLead || null;
            // CRITICAL: Clear dialingLeadIdRef so the next lead can be dialed
            // Without this, the next handleCall() sees the same lead ID and skips it
            dialingLeadIdRef.current = null;
            setCurrentIndex(nextIndex);
            setTimeout(() => {
              isNavigatingRef.current = false;
            }, 100);
          }
        } else {
          // If we didn't advance (paused or other condition), still clear the call ID
          setCurrentCallId(null);
        }
      }, autoAdvanceDelay); // 500ms for normal calls, 2500ms for short answered calls (AMD may still report)

      return () => {
        clearTimeout(timer);
        shouldAutoAdvanceRef.current = false;
      };
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- isPaused and isCallAnswered intentionally
    // excluded: the setTimeout callback reads isPausedRef.current and callResultsRef/isCallAnsweredRef
    // for up-to-date values. Including them as deps causes the effect to re-run when they change
    // (e.g., isCallAnswered resets to false on call end), which clears the pending auto-advance timer.
    // CRITICAL FIX: Use currentLead?.id instead of currentLead to prevent timer cancellation
    // when the leads array is recreated by React Query refetch (object reference changes but
    // lead ID stays the same). Timer cancellation = auto-advance broken = Brandon's bug.
  }, [activeCall, currentCallId, currentLead?.id]);

  // Auto-dial when advancing to next lead (triggered by index change from call end or skip)
  // Also re-triggers when activeCall changes to null (after disconnect completes)
  useEffect(() => {
    // CRITICAL: Check sessionStoppedRef IMMEDIATELY using the ref, not just in the timeout
    // This prevents the race condition where the timeout is scheduled before the session is marked as stopped
    if (sessionStoppedRef.current) {
      return;
    }

    // CRITICAL: Use currentIndexRef for bounds check to avoid stale closure
    // The state-based currentIndex might be behind the ref which is updated synchronously
    const currentIdx = currentIndexRef.current;

    // Don't auto-dial on first load (index 0) - that's handled by autoStartFirstCall
    // Don't auto-dial if paused, already dialing, or session has been stopped
    // Don't auto-dial if we've reached or exceeded the last lead
    if (
      currentIdx > 0 &&
      currentIdx < leads.length &&
      !isPaused &&
      !isDialing &&
      !activeCall &&
      isDeviceReady &&
      hasMicrophone !== false &&
      hasActiveNumbers
    ) {
      const timer = setTimeout(() => {
        // CRITICAL: If auto-advance retry logic is still deciding whether to retry,
        // don't race ahead and dial the next lead. The retry check (500ms timer) will
        // either retry the same lead or advance and unblock this.
        // BUG FIX: Add a safety fallback — if pendingRetryCheckRef has been stuck for >5s,
        // force-reset it. This prevents permanent stall when React effect cleanup clears
        // the auto-advance timer before the retry decision code runs.
        if (pendingRetryCheckRef.current) {
          if (pendingRetryCheckSetAtRef.current && Date.now() - pendingRetryCheckSetAtRef.current > 5000) {
            console.warn('[PowerDialer] Safety: pendingRetryCheckRef stuck for >5s — force-resetting');
            pendingRetryCheckRef.current = false; pendingRetryCheckSetAtRef.current = null;
            // Fall through to dial
          } else {
            return;
          }
        }

        // CRITICAL: Use refs inside timeout to get CURRENT values, not stale closure values
        // This prevents dialing when session has ended but effect had stale state
        const currentIdxNow = currentIndexRef.current;
        const leadAtCurrentIdx = leads[currentIdxNow];
        const leadInRef = currentLeadRef.current;

        // SAFEGUARD: Verify the lead at index matches what's in our ref
        if (leadAtCurrentIdx && leadInRef && leadAtCurrentIdx.id !== leadInRef.id) {
          console.error('[PowerDialer] Auto-dial MISMATCH: index lead differs from ref lead', {
            indexLead: leadAtCurrentIdx.id,
            refLead: leadInRef.id,
            currentIdx: currentIdxNow,
          });
          // Sync the ref to match the index
          currentLeadRef.current = leadAtCurrentIdx;
        }

        // Call-waiting guard — see comment in the other auto-advance path above.
        const callWaitingActive2 = typeof window !== 'undefined'
          && sessionStorage.getItem('__callwaiting_active') === '1';
        if (
          !isDialing &&
          !activeCall &&
          !isPausedRef.current &&
          !sessionStoppedRef.current &&
          isDeviceReady && // Re-check device is still ready (could be reconnecting)
          currentIdxNow < leads.length && // Re-check bounds with current value
          leadAtCurrentIdx && // Ensure lead exists at index
          !callWaitingActive2
        ) {
          handleCall();
        }
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [currentIndex, activeCall, isDeviceReady]); // Trigger on index change, activeCall becomes null, OR device recovers from reconnect

  // Track if session has actually started (made at least one call attempt)
  const hasSessionStartedRef = useRef(false);

  // Mark session as started when we make the first call
  useEffect(() => {
    if (Object.keys(callResults).length > 0) {
      hasSessionStartedRef.current = true;
    }
  }, [callResults]);

  useEffect(() => {
    // Guard: Don't trigger completion if:
    // 1. leads array is empty (initial mount or context not synced)
    // 2. session hasn't actually started (no calls made yet)
    // 3. completion already fired (prevents double onComplete from re-renders)
    // NOTE: We do NOT check sessionStoppedRef here because the auto-advance effect sets it
    // to true for the last lead (to prevent auto-dial race), which would block natural completion.
    // The backend guard prevents 'completed' from overwriting 'stopped' if user manually stopped.
    if (leads.length > 0 && currentIndex >= leads.length && hasSessionStartedRef.current && !completionFiredRef.current) {
      // CRITICAL: Mark as fired IMMEDIATELY to prevent double-firing
      completionFiredRef.current = true;
      sessionStoppedRef.current = true;
      isPausedRef.current = true;
      setIsInPowerDialerMode(false); // Allow incoming calls again

      // Calculate stats from callResultsRef (use ref for latest values, avoids stale closure)
      const results = Object.values(callResultsRef.current);
      const totalDialTime = getDialSeconds();

      const offset = resumeOffsetRef.current;
      const stats = {
        answered: results.filter(r => r.result === "answered" || r.wasAnswered).length,
        called: results.length + offset,
        totalDialTime,
        appointments: results.filter(r => r.leadStatus === 'Appt').length,
        underwriting: results.filter(r => r.leadStatus === 'Underwriting').length,
        sold: results.filter(r => r.leadStatus === 'Sold & Approved').length,
      };

      // Persist completion to backend (fire-and-forget)
      if (backendSessionIdRef.current) {
        dialerSessionApi
          .completeSession(backendSessionIdRef.current, {
            status: 'completed',
            currentLeadIndex: currentIndexRef.current,
            completedCalls: results.length + offset,
            totalAnswered: stats.answered,
            totalFailed: results.filter(r => r.result === 'failed').length,
            totalVoicemail: results.filter(r => r.result === 'voicemail').length,
            totalSkipped: results.filter(r => r.result === 'skipped').length,
            totalDialTime: stats.totalDialTime,
            outcome: {
              appointments: stats.appointments,
              underwriting: stats.underwriting,
              sold: stats.sold,
            },
          })
          .catch(() => {}); // Silently ignore
      }

      // Clear persisted call results since session is complete
      if (typeof window !== 'undefined') {
        localStorage.removeItem(CALL_RESULTS_STORAGE_KEY);
      }

      onComplete(stats);
    }
  }, [currentIndex, leads.length, onComplete, leads]);

  const updateFormData = (field: keyof Lead, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handlePhoneFallbackCall = async () => {
    if (!currentLead) return;

    setIsDialing(true);
    setCallStatusOverride(null); // Clear voicemail override for new call
    setAppleScreeningDetected(false);

    try {
      // Create session record for power dialer (phone forwarding fallback)
      const sessionResponse = await api.post({
        url: getApiUrl('/communication/sessions/manual-call'),
        body: {
          toNumber: currentLead.phone,
          leadId: currentLead.id,
          dialerSessionId: backendSessionIdRef.current || undefined,
        },
        auth: true,
      });

      // Use callService to initiate phone-to-phone call
      const callServiceModule = await import("@/app/_module/services/callService");
      const result = await callServiceModule.callService.initiateCall({
        to: currentLead.phone,
        from: undefined,
        lead_id: currentLead.id,
        recording_enabled: true,
        metadata: {
          lead_name: currentLead.name,
          lead_email: currentLead.email || "",
          session_type: "power_dial_fallback",
        }
      });

      if (result && result.call_id) {
        setCurrentCallId(result.call_id);

        // Mark as call initiated (will be updated to answered/called when call completes)
        setCallResults((prev) => ({
          ...prev,
          [currentLead.id]: {
            leadId: currentLead.id,
            result: "called", // Initiated - will change to "answered" if customer picks up
            callId: result.call_id,
            callStatus: result.status,
          },
        }));

      }
    } catch (error: any) {
      setIsDialing(false);

      // TCPA calling-hours block (425) — mark as "skipped (outside hours)", NOT
      // a failure, so session stats stay clean, and tell the agent why.
      const resp = error?.response;
      const isHoursBlock =
        resp?.status === 425 ||
        resp?.data?.code === 'OUTSIDE_CALLING_HOURS' ||
        resp?.data?.data?.code === 'OUTSIDE_CALLING_HOURS';

      if (isHoursBlock) {
        setCallResults((prev) => ({
          ...prev,
          [currentLead.id]: {
            leadId: currentLead.id,
            result: "skipped",
            callStatus: "skipped_outside_hours",
          },
        }));
        toast.info(
          `Skipped ${currentLead.name || 'lead'} — outside calling hours (8am–9pm their local time)`,
        );
      } else {
        // Mark as failed
        setCallResults((prev) => ({
          ...prev,
          [currentLead.id]: {
            leadId: currentLead.id,
            result: "failed",
            callStatus: "failed",
          },
        }));
      }
    }
  };

  const handleCall = async () => {
    // CRITICAL: Prevent duplicate calls with a lock mechanism
    if (isCallInProgressRef.current) {
      return;
    }
    isCallInProgressRef.current = true;

    // Use ref for paused check to avoid stale closure issues (especially when called from handleResume)
    // Also check sessionStoppedRef to prevent calls after session ends
    // CRITICAL: Use currentIndexRef for bounds check to get the SYNCHRONOUS value, not the potentially stale state
    if (isPausedRef.current || sessionStoppedRef.current || currentIndexRef.current >= leads.length) {
      isCallInProgressRef.current = false;
      return;
    }

    // LAST-LINE GUARD: if call-waiting is active, do NOT dial. Every upstream
    // check above should already block this, but in case any path slips
    // through (retry fired before flag check, stale ref, etc.), this is the
    // safety net closest to the actual Twilio dial. The held lead must NOT
    // be re-dialed while they're on hold music or during the handoff window.
    if (typeof window !== 'undefined' && sessionStorage.getItem('__callwaiting_active') === '1') {
      console.warn('[PowerDialer] handleCall aborted — call-waiting active');
      isCallInProgressRef.current = false;
      return;
    }

    // CRITICAL FIX: Use currentLeadRef.current as the source of truth instead of currentLead
    // The currentLead from React state can be stale when this function is called from a setTimeout
    // (e.g., from autoDialNextLead). The ref is updated synchronously in advanceToNextLead.
    const leadToCall = currentLeadRef.current;

    // Early return if no lead to call
    if (!leadToCall) {
      isCallInProgressRef.current = false;
      return;
    }

    // SAFEGUARD: Prevent dialing the same lead twice (race condition protection)
    if (dialingLeadIdRef.current === leadToCall.id) {
      isCallInProgressRef.current = false;
      return;
    }

    // SAFEGUARD #1: Verify the lead at currentIndex matches our leadToCall
    // This prevents calling the wrong lead if the leads array was reordered or modified
    const currentIdx = currentIndexRef.current;
    const leadAtIndex = leads[currentIdx];
    if (!leadAtIndex || leadAtIndex.id !== leadToCall.id) {
      console.error('[PowerDialer] MISMATCH DETECTED: Lead at index does not match leadToCall', {
        currentIndex: currentIdx,
        leadAtIndexId: leadAtIndex?.id,
        leadToCallId: leadToCall.id,
        leadAtIndexName: leadAtIndex?.name,
        leadToCallName: leadToCall.name,
      });
      // Try to find the correct index for this lead
      const correctIndex = leads.findIndex((l: Lead) => l.id === leadToCall.id);
      if (correctIndex !== -1 && correctIndex !== currentIdx) {
        currentIndexRef.current = correctIndex;
        setCurrentIndex(correctIndex);
      }
      isCallInProgressRef.current = false;
      return;
    }

    // SAFEGUARD #2: Double-check lead ID exists and has required fields
    if (!leadToCall.id || typeof leadToCall.id !== 'string') {
      console.error('[PowerDialer] Invalid lead ID, skipping:', leadToCall);
      isCallInProgressRef.current = false;
      advanceToNextLead(true);
      return;
    }

    // Check for microphone — pause session so agent sees why calls stopped
    if (hasMicrophone === false) {
      console.warn('[PowerDialer] No microphone detected — pausing session');
      isCallInProgressRef.current = false;
      setIsPaused(true);
      isPausedRef.current = true;
      setFailureToast('No microphone detected — session paused');
      return;
    }

    // CRITICAL: Check if user has active phone numbers before allowing calls
    if (!hasActiveNumbers) {
      console.warn('[PowerDialer] No active phone numbers — pausing session');
      isCallInProgressRef.current = false;
      setIsPaused(true);
      isPausedRef.current = true;
      setFailureToast('No active phone numbers — session paused');
      return;
    }

    // Debounce: Prevent duplicate calls within 3 seconds (increased from 2s for safety)
    const now = Date.now();
    if (now - lastCallTimeRef.current < 3000) {
      isCallInProgressRef.current = false;
      return;
    }
    lastCallTimeRef.current = now;

    if (!leadToCall.phone) {
      console.log('[PowerDialer] Lead has no phone number, marking as failed:', leadToCall.id);
      isCallInProgressRef.current = false;
      setCallResults((prev) => ({
        ...prev,
        [leadToCall.id]: { leadId: leadToCall.id, result: "failed" as const },
      }));
      setFailureToast('No phone number — skipped');
      dialingLeadIdRef.current = null;
      lastCallTimeRef.current = 0;
      advanceToNextLead(true);
      return;
    }

    if (!validatePhoneNumber(leadToCall.phone)) {
      console.log('[PowerDialer] Invalid phone number, marking as failed:', leadToCall.phone);
      isCallInProgressRef.current = false;
      setCallResults((prev) => ({
        ...prev,
        [leadToCall.id]: { leadId: leadToCall.id, result: "failed" as const },
      }));
      setFailureToast('Invalid number — skipped');
      dialingLeadIdRef.current = null;
      lastCallTimeRef.current = 0;
      advanceToNextLead(true);
      return;
    }

    const cleanedPhone = leadToCall.phone.replace(/\D/g, '');

    // Phone number dedup: Skip if we already called this number in this session
    // (Different leads can share the same phone — e.g. spouse/household duplicates)
    // Allow retries of the SAME lead (retryCountRef tracks that separately)
    const currentRetries = retryCountRef.current[leadToCall.id] || 0;
    if (currentRetries === 0 && calledPhoneNumbersRef.current.has(cleanedPhone)) {
      console.log('[PowerDialer] Skipping duplicate phone number:', cleanedPhone, 'for lead:', leadToCall.id);
      isCallInProgressRef.current = false;
      // Mark as called (duplicate) and advance
      setCallResults((prev) => ({
        ...prev,
        [leadToCall.id]: {
          leadId: leadToCall.id,
          result: "called" as const,
          wasAnswered: false,
        },
      }));
      dialingLeadIdRef.current = null;
      lastCallTimeRef.current = 0;
      advanceToNextLead(true);
      return;
    }

    // Call forwarding is ONLY for inbound calls (simultaneous ring)
    // For outbound calls from Power Dialer, ALWAYS use browser calling
    // This ensures agents don't have to answer their phone for every lead in the session

    // Use browser calling (modern method)
    if (!isDeviceReady) {
      console.warn('[PowerDialer] Twilio device not ready — pausing session');
      isCallInProgressRef.current = false;
      setIsPaused(true);
      isPausedRef.current = true;
      setFailureToast('Call device not ready — session paused');
      return;
    }

    setIsDialing(true);
    isDialingRef.current = true; // Synchronous — handleEndCall reads this immediately
    setCallStatusOverride(null); // Clear voicemail override for new call
    setAppleScreeningDetected(false);
    // Reset last call duration for the new call
    lastCallDurationRef.current = 0;

    // CRITICAL FIX: Set leadBeingCalledRef BEFORE the call starts
    // This ensures customer_answered event uses the correct lead, not the next one
    leadBeingCalledRef.current = leadToCall;

    // SAFEGUARD: Track which lead we're dialing to prevent duplicate dial attempts
    dialingLeadIdRef.current = leadToCall.id;

    // Track that we attempted a call for this lead — if the call is rejected by carrier
    // before Twilio 'accept' fires, currentCallId will never be set, and auto-advance
    // needs this ref to know a call was attempted and ended.
    callAttemptedForLeadRef.current = leadToCall.id;

    // Track this phone number as called for dedup
    calledPhoneNumbersRef.current.add(cleanedPhone);

    try {
      // Create session record for power dialer (browser calling)
      try {
        const sessionResponse = await api.post({
          url: getApiUrl('/communication/sessions/manual-call'),
          body: {
            toNumber: cleanedPhone,
            leadId: leadToCall.id,
            // Our run's id: the backend keeps this call on it instead of sweeping it as idle.
            dialerSessionId: backendSessionIdRef.current || undefined,
          },
          auth: true,
        });
      } catch (sessionError) {
        console.error('[PowerDialer] Session creation failed, marking lead as failed:', sessionError);
        setIsDialing(false);
        leadBeingCalledRef.current = null;
        dialingLeadIdRef.current = null;
        isCallInProgressRef.current = false;
        callAttemptedForLeadRef.current = null;

        // A trial-cap 403 is NOT a per-lead failure. Treating it as one would
        // mark this lead failed, auto-advance, hit the same 403 on the next
        // lead, and so on down the whole queue — every lead "failed" in
        // seconds, and the only thing the agent ever sees is "skipping".
        // Show the server's plain-English reason and stop the run here; the
        // lead stays untouched so it is still there after they upgrade.
        const capMsg =
          sessionError instanceof Error && /limit/i.test(sessionError.message)
            ? sessionError.message
            : null;
        if (capMsg) {
          setFailureToast(capMsg);
          lastCallTimeRef.current = 0;
          return;
        }

        // Mark as failed so agent sees it and auto-advance
        setCallResults((prev) => ({
          ...prev,
          [leadToCall.id]: { leadId: leadToCall.id, result: "failed" as const },
        }));
        setFailureToast('Call failed — skipping');
        lastCallTimeRef.current = 0;
        advanceToNextLead(true);
        return;
      }

      // CRITICAL: Check if agent skipped/ended while we were awaiting the API call.
      // Two checks: (1) lead changed (Skip advanced to next lead)
      //             (2) callCancelled (End before connect, retry pending for same lead)
      if (currentLeadRef.current?.id !== leadToCall.id) {
        setIsDialing(false);
        leadBeingCalledRef.current = null;
        dialingLeadIdRef.current = null;
        isCallInProgressRef.current = false;
        callAttemptedForLeadRef.current = null;
        return;
      }

      // ─── DialIQ Predictive branch ────────────────────────────────────────
      // When predictive is active AND the agent is connected to the persistent
      // conference, we DON'T do device.connect() — that would tear down the
      // conference connection. Instead, the backend adds the customer to the
      // existing conference. Audio mixes automatically.
      const predictiveCtx = predictiveRef.current;
      const usePredictiveDial =
        predictiveCtx.isPredictiveActive && predictiveCtx.isAgentInConference;

      if (usePredictiveDial) {
        // Wait for any in-flight end-call→bridge sequence to settle. The
        // IIFE in handleEndCall sets preBridgedLeadIdRef on success — we
        // must observe that AFTER it lands, otherwise we'd race past and
        // dial the lead a second time.
        if (pendingEndCallBridgeRef.current) {
          try {
            await pendingEndCallBridgeRef.current;
          } catch { /* refs got set or not — proceed to check below */ }
        }

        // CRITICAL: if this lead was just bridged in via handleAgentCallEnded
        // (predial → bridge path), they're already on the line. DON'T dial
        // them again — that would create a duplicate outbound call.
        // Adopt the bridged customer's SID as the active one and skip dial.
        if (preBridgedLeadIdRef.current === leadToCall.id && preBridgedCustomerCallSidRef.current) {
          const bridgedSid = preBridgedCustomerCallSidRef.current;
          preBridgedLeadIdRef.current = null;
          preBridgedCustomerCallSidRef.current = null;

          predictiveCustomerCallSidRef.current = bridgedSid;
          setCurrentCallId(bridgedSid);
          setCallResults((prev) => ({
            ...prev,
            [leadToCall.id]: {
              leadId: leadToCall.id,
              result: "called",
              callStatus: "in-progress",
              callId: bridgedSid,
            },
          }));
          setIsDialing(false);
          // isCallAnswered will flip true (or already has) via the
          // customer_answered Supabase broadcast for this CallSid.
          return;
        }

        const dialResult = await predictiveCtx.dialLead(leadToCall, selectedAgentPhone || '');

        if (!dialResult) {
          // dialLead failed — mark lead failed + advance.
          setIsDialing(false);
          leadBeingCalledRef.current = null;
          dialingLeadIdRef.current = null;
          isCallInProgressRef.current = false;
          callAttemptedForLeadRef.current = null;
          setCallResults((prev) => ({
            ...prev,
            [leadToCall.id]: { leadId: leadToCall.id, result: "failed" as const },
          }));
          setFailureToast('Predictive dial failed — skipping');
          lastCallTimeRef.current = 0;
          advanceToNextLead(true);
          return;
        }

        // Re-check lead didn't change mid-await
        if (currentLeadRef.current?.id !== leadToCall.id) {
          // Hang up the customer we just dialed — agent moved on.
          predictiveCtx.endCustomerLeg(dialResult.customerCallSid).catch(() => {});
          setIsDialing(false);
          leadBeingCalledRef.current = null;
          dialingLeadIdRef.current = null;
          isCallInProgressRef.current = false;
          callAttemptedForLeadRef.current = null;
          return;
        }

        // Track the customer leg for later end-customer-leg call.
        predictiveCustomerCallSidRef.current = dialResult.customerCallSid;

        // CRITICAL: bind currentCallId to the CUSTOMER's CallSid (not the
        // conference SID). Disposition save, recording API, call_histories
        // linkage all key off this. The activeCallSid sync effect skips
        // updates while predictive is active, so this is the authoritative
        // source for the call SID during predictive mode.
        setCurrentCallId(dialResult.customerCallSid);

        // Mark UI as "called" — customer-status webhook → socket events will
        // update this to "answered" / AMD events / etc. (Same downstream flow
        // as standard dial — only the dial mechanism differs.)
        setCallResults((prev) => ({
          ...prev,
          [leadToCall.id]: {
            leadId: leadToCall.id,
            result: "called",
            callStatus: "connecting",
            callId: dialResult.customerCallSid,
          },
        }));

        setIsDialing(false);
        return;
      }

      // ─── Standard browser-dial flow (manual, multi-line, non-DialIQ) ─────
      // Initiate browser-based call using Twilio Voice SDK
      const call = await connect({
        to: cleanedPhone,
        // Power dialer: always let backend do smart area code matching per lead
        // Don't send 'from' — it becomes SelectedCallerId which overrides matching
        // displayFrom shows the agent's preferred number initially, corrected to actual
        // caller ID when customer_answered event arrives from backend
        displayFrom: selectedAgentPhone || undefined,
        leadId: leadToCall.id,
        metadata: {
          source: "power_dialer",
          phoneNumber: formatPhoneNumber(leadToCall.phone),
          leadName: leadToCall.name || "Unknown",
          lead_name: leadToCall.name,
          lead_email: leadToCall.email || "",
          session_type: "power_dial",
        }
      });

      if (!call) {
        throw new Error("Failed to initiate call");
      }

      // CRITICAL: Check if agent skipped/ended while we were awaiting connect().
      // If lead changed or call cancelled, disconnect the just-created call and abort.
      if (currentLeadRef.current?.id !== leadToCall.id) {
        try { hangupCall(call); } catch { /* ignore */ }
        setIsDialing(false);
        leadBeingCalledRef.current = null;
        dialingLeadIdRef.current = null;
        isCallInProgressRef.current = false;
        callAttemptedForLeadRef.current = null;
        return;
      }

      // Don't set callId here - it will be undefined initially
      // The callId will be set via the activeCallSid sync useEffect when the call is accepted
      // and the CallSid becomes available

      // Mark as call initiated (will be updated to answered/called when call completes)
      setCallResults((prev) => ({
        ...prev,
        [leadToCall.id]: {
          leadId: leadToCall.id,
          result: "called", // Initiated - will change to "answered" if customer picks up
          callStatus: "connecting",
        },
      }));

      // DTMF listening will auto-start via useEffect when call status changes to 'in-progress'
      // Recording is manual - user must click the record button

    } catch (error) {
      console.error('[PowerDialer] Call connect failed, marking as failed and advancing:', error);
      setIsDialing(false);
      leadBeingCalledRef.current = null;
      dialingLeadIdRef.current = null;
      isCallInProgressRef.current = false;

      // Mark as failed and auto-advance so dialer doesn't get stuck
      setCallResults((prev) => ({
        ...prev,
        [leadToCall.id]: { leadId: leadToCall.id, result: "failed" },
      }));
      setFailureToast('Call failed — skipping');
      lastCallTimeRef.current = 0;
      advanceToNextLead(true);
    }
  };

  // Auto-dial the next lead after advancing (speed dial mode)
  // Define BEFORE handleSkip and handleMarkFailed to avoid forward reference
  // CRITICAL: We use currentLeadRef to avoid stale closure issues
  const autoDialNextLead = useCallback(() => {
    // CRITICAL: Check if session has been stopped
    if (isPausedRef.current || sessionStoppedRef.current) return;

    // Delay to let React state settle and re-render with the new currentIndex
    // This ensures currentLead is updated before we call handleCall
    setTimeout(() => {
      // CRITICAL: Re-check paused AND stopped state using refs (avoids stale closure)
      if (isPausedRef.current || sessionStoppedRef.current) return;

      // CRITICAL: Check currentIndex bounds using ref
      if (currentIndexRef.current >= leads.length) return;

      // Use ref to get the CURRENT lead (not stale closure value)
      const leadToCall = currentLeadRef.current;
      if (leadToCall && isDeviceReady && hasMicrophone !== false && hasActiveNumbers) {
        // Double-check the lead we're about to call matches what's displayed
        // This prevents calling the wrong lead if state hasn't settled
        handleCall();
      }
    }, 600); // Slightly longer delay to ensure React has re-rendered with new index
  }, [isDeviceReady, hasMicrophone, hasActiveNumbers, handleCall, leads.length]);

  const advanceToNextLead = useCallback((shouldAutoDial: boolean = true) => {
    // Prevent queue buildup - if already navigating, ignore
    if (isNavigatingRef.current) {
      return;
    }

    isNavigatingRef.current = true;

    // Calculate next index and update state
    const nextIndex = currentIndex + 1;

    // CRITICAL: If this is the last lead, mark session as stopped BEFORE incrementing
    // This prevents the auto-dial effect from firing during the race window
    if (nextIndex >= leads.length) {
      sessionStoppedRef.current = true;
    }

    // CRITICAL: Update BOTH refs SYNCHRONOUSLY before state update to prevent stale closure race
    // handleCall uses currentLeadRef.current to get the lead to dial
    currentIndexRef.current = nextIndex;
    const nextLead = leads[nextIndex] || null;
    currentLeadRef.current = nextLead;
    // Clear the dialingLeadIdRef so the new lead can be dialed
    // This is the proper place to clear it - when we advance to a new lead
    dialingLeadIdRef.current = null;
    setCurrentIndex(nextIndex);

    // Schedule the navigation lock reset and auto-dial after state update
    // Use longer delay to ensure React has re-rendered with new currentLead
    setTimeout(() => {
      isNavigatingRef.current = false;

      // Auto-dial next lead if not paused (use ref to avoid stale closure)
      if (shouldAutoDial && !isPausedRef.current) {
        autoDialNextLead();
      }
    }, 300); // Allow time for React to re-render with new index
  }, [currentIndex, leads, autoDialNextLead]);

  // Advance to the next lead after a MANUAL voicemail drop, WITHOUT disconnecting
  // the current customer leg. The drop endpoint already redirected that leg to
  // <Play>voicemail<Hangup/>, so it leaves the conference and plays out on its
  // own — calling disconnect() here (like handleSkip does) would cut it off.
  // Mirrors handleSkip's state resets minus the disconnect, and records the
  // result as "voicemail". The next customer dials into the persistent
  // conference via advanceToNextLead; the previous leg is never touched.
  const dropVoicemailAdvance = useCallback(() => {
    const lead = currentLeadRef.current;
    manuallyAdvancedRef.current = true;
    awaitingDispositionRef.current = false;
    setAwaitingDisposition(false);
    setIsWaitingForRing(false);
    setIsDialing(false);
    setIsCallAnswered(false);
    setCurrentCallId(null);
    setCallStatusOverride(null);
    isCallInProgressRef.current = false;
    lastCallTimeRef.current = 0;
    if (lead) {
      setCallResults((prev) => ({
        ...prev,
        [lead.id]: { leadId: lead.id, result: "voicemail" as const },
      }));
    }
    advanceToNextLead(true);
  }, [advanceToNextLead]);

  const handleSkip = useCallback(() => {
    if (!currentLead) return;

    // Spam protection: Debounce rapid clicks (500ms minimum between clicks)
    const now = Date.now();
    if (now - lastSkipTimeRef.current < SKIP_DEBOUNCE_MS) {
      return;
    }
    lastSkipTimeRef.current = now;

    // Clear disposition pause if active (agent clicked Skip while taking notes)
    awaitingDispositionRef.current = false;
    setAwaitingDisposition(false);

    // CRITICAL: Set manuallyAdvancedRef to prevent the auto-advance effect (line 458) from
    // incrementing currentIndex again. The auto-advance effect checks this flag and skips
    // if we've already manually advanced. The auto-dial useEffect (line 538) doesn't check
    // this flag - it just calls handleCall() when activeCall becomes null.
    manuallyAdvancedRef.current = true;

    // If there's an active call (ringing or connected), disconnect it first.
    // userInitiated: true — agent clicked Skip, so the post-disconnect toast
    // should reflect agent action ("You ended the call with X"), not "X hung up".
    if (activeCall) {
      disconnect({ userInitiated: true });
    }

    // Also reset the waiting for ring state since we're skipping
    setIsWaitingForRing(false);

    setCallResults((prev) => ({
      ...prev,
      [currentLead.id]: { leadId: currentLead.id, result: "skipped" },
    }));

    // Reset states immediately
    setIsDialing(false);
    setIsCallAnswered(false);
    setCurrentCallId(null);
    // Release call lock so next call can proceed
    isCallInProgressRef.current = false;

    // CRITICAL: Reset the debounce timer so the next call isn't blocked
    // The debounce prevents duplicate calls within 3s, but skipping should allow immediate next call
    lastCallTimeRef.current = 0;

    // Check if there's a next lead
    const nextIndex = currentIndex + 1;
    if (nextIndex < leads.length) {
      // Advance to next lead - set navigating flag temporarily
      isNavigatingRef.current = true;

      // CRITICAL: Update the refs SYNCHRONOUSLY before state update
      // This ensures the useEffect sees the correct values even before React re-renders
      const nextLead = leads[nextIndex];

      // SAFEGUARD: Verify next lead exists
      if (!nextLead) {
        console.error('[PowerDialer] handleSkip: Next lead is undefined at index', nextIndex);
        isNavigatingRef.current = false;
        return;
      }

      currentLeadRef.current = nextLead;
      currentIndexRef.current = nextIndex;
      // CRITICAL: Clear dialingLeadIdRef so the next lead can be dialed
      // Without this, the next handleCall() sees the same lead ID and skips it
      dialingLeadIdRef.current = null;

      setCurrentIndex(nextIndex);

      // Reset navigating flag and manuallyAdvancedRef after navigation completes
      // CRITICAL FIX: manuallyAdvancedRef must be cleared here because handleSkip clears
      // currentCallId (line 1465) before disconnect fires. When disconnect finally comes,
      // the auto-advance effect condition (!activeCall && currentCallId) is FALSE (callId is null),
      // so it never enters the if block that normally clears manuallyAdvancedRef (line 785).
      // Result: manuallyAdvancedRef leaks into the NEXT call and blocks its auto-advance.
      setTimeout(() => {
        isNavigatingRef.current = false;
        manuallyAdvancedRef.current = false;
      }, 100);
    } else {
      advanceToNextLead(false); // No auto-dial, session ending
    }
  }, [currentLead, activeCall, disconnect, currentIndex, leads, advanceToNextLead]);

  const handleMarkFailed = useCallback(() => {
    if (!currentLead) return;

    // Spam protection: Debounce rapid clicks (500ms minimum between clicks)
    const now = Date.now();
    if (now - lastSkipTimeRef.current < SKIP_DEBOUNCE_MS) {
      return;
    }
    lastSkipTimeRef.current = now;

    // CRITICAL: Set manuallyAdvancedRef to prevent the auto-advance effect (line 458) from
    // incrementing currentIndex again. The auto-advance effect checks this flag and skips
    // if we've already manually advanced. The auto-dial useEffect (line 538) doesn't check
    // this flag - it just calls handleCall() when activeCall becomes null.
    manuallyAdvancedRef.current = true;

    // If there's an active call (ringing or connected), disconnect it first.
    // userInitiated: true — agent marked the lead as failed, agent-driven action.
    if (activeCall) {
      disconnect({ userInitiated: true });
    }

    // Also reset the waiting for ring state since we're marking as failed
    setIsWaitingForRing(false);

    setCallResults((prev) => ({
      ...prev,
      [currentLead.id]: { leadId: currentLead.id, result: "failed" },
    }));

    // Reset states immediately
    setIsDialing(false);
    setIsCallAnswered(false);
    setCurrentCallId(null);
    // Release call lock so next call can proceed
    isCallInProgressRef.current = false;

    // CRITICAL: Reset the debounce timer so the next call isn't blocked
    // The debounce prevents duplicate calls within 3s, but marking failed should allow immediate next call
    lastCallTimeRef.current = 0;

    // Check if there's a next lead
    const nextIndex = currentIndex + 1;
    if (nextIndex < leads.length) {
      // Advance to next lead - set navigating flag temporarily
      isNavigatingRef.current = true;

      // CRITICAL: Update the refs SYNCHRONOUSLY before state update
      // This ensures the useEffect sees the correct values even before React re-renders
      const nextLead = leads[nextIndex];

      // SAFEGUARD: Verify next lead exists
      if (!nextLead) {
        console.error('[PowerDialer] handleMarkFailed: Next lead is undefined at index', nextIndex);
        isNavigatingRef.current = false;
        return;
      }

      currentLeadRef.current = nextLead;
      currentIndexRef.current = nextIndex;
      // CRITICAL: Clear dialingLeadIdRef so the next lead can be dialed
      // Without this, the next handleCall() sees the same lead ID and skips it
      dialingLeadIdRef.current = null;

      setCurrentIndex(nextIndex);

      // Reset navigating flag and manuallyAdvancedRef after navigation completes
      // Same fix as handleSkip — clear manuallyAdvancedRef so it doesn't leak into next call
      setTimeout(() => {
        isNavigatingRef.current = false;
        manuallyAdvancedRef.current = false;
      }, 100);
    } else {
      advanceToNextLead(false); // No auto-dial, session ending
    }
  }, [currentLead, activeCall, disconnect, currentIndex, leads, advanceToNextLead]);

  // End call and let auto-advance handle moving to next lead
  const handleEndCall = useCallback(() => {
    // Spam protection: Debounce rapid clicks
    const now = Date.now();
    if (now - lastSkipTimeRef.current < SKIP_DEBOUNCE_MS) {
      return;
    }
    lastSkipTimeRef.current = now;

    // NOTE: the predictive bridge for a pre-dialed customer is fired AFTER
    // we hang up the current customer's leg (see persistent-conference branch
    // below). Sequencing prevents both customers from being in the conference
    // simultaneously — privacy-critical, the new lead must not overhear the
    // tail end of the previous call.

    // RETRY-OR-ADVANCE helper. Used when we end a call early (Path 1 / Path 3
    // below) and the auto-advance useEffect won't fire its own retry logic
    // because it requires `currentCallId` to be set (which only happens after
    // Twilio's 'accept' event). If the user configured dialAttempts > 1 and
    // hasn't exceeded retries for this lead, re-dial the same lead; otherwise
    // advance to next.
    const retryOrAdvance = (leadId: string) => {
      const maxRetries = dialerPreferencesRef.current.dialAttempts - 1;
      const currentRetries = retryCountRef.current[leadId] || 0;
      if (maxRetries > 0 && currentRetries < maxRetries) {
        retryCountRef.current[leadId] = currentRetries + 1;
        setIsRetryAttempt(true);
        // Keep result as "called" with wasAnswered=false so retry starts clean.
        callResultsRef.current = {
          ...callResultsRef.current,
          [leadId]: {
            ...callResultsRef.current[leadId],
            leadId,
            result: "called" as const,
            wasAnswered: false,
          },
        };
        // Block auto-dial during the 1500ms retry window. Without this, the
        // 400ms auto-dial timer at line ~1134 races ahead, advances to the
        // next lead, and our delayed handleCall() either no-ops or dials the
        // wrong lead. Symptom was: click End during ringing, session skips
        // the current lead instead of retrying.
        pendingRetryCheckRef.current = true;
        pendingRetryCheckSetAtRef.current = Date.now();
        // Reset locks so handleCall can re-dial the same lead.
        dialingLeadIdRef.current = null;
        isCallInProgressRef.current = false;
        lastCallTimeRef.current = 0;
        sessionStoppedRef.current = false;
        setTimeout(() => {
          setIsRetryAttempt(false);
          pendingRetryCheckRef.current = false;
          pendingRetryCheckSetAtRef.current = null;
          if (!isPausedRef.current && !sessionStoppedRef.current && isDeviceReady && isInPowerDialerModeRef.current) {
            handleCall();
          } else if (!isPausedRef.current && !sessionStoppedRef.current) {
            advanceToNextLead(true);
          }
        }, 1500);
      } else {
        advanceToNextLead(true);
      }
    };

    // ─── DialIQ Predictive branch ─────────────────────────────────────────
    // In predictive mode, activeCall is the persistent conference connection.
    // We MUST NOT disconnect it — that would kick the agent out of the
    // conference and break the next bridge. Instead, end only the customer's
    // leg; agent stays in the conference, ready for the next dial.
    //
    // CRITICAL ORDERING (privacy): await endCustomerLeg FIRST so the current
    // customer is gone from the conference, THEN fire the bridge to bring
    // the pre-dialed next customer in. If we ran them in parallel, the next
    // customer could briefly hear the tail of the previous conversation.
    const predictiveCtxForEnd = predictiveRef.current;
    if (
      predictiveCtxForEnd.isPredictiveActive &&
      predictiveCtxForEnd.isAgentInConference
    ) {
      const customerSid = predictiveCustomerCallSidRef.current;
      predictiveCustomerCallSidRef.current = null;

      // Fire the sequenced cleanup + bridge in the background. handleEndCall
      // itself returns sync to keep the UI snappy. Store the promise so
      // handleCall can await it (avoids duplicate-dial race when bridge
      // takes longer than autoDialNextLead's 600ms timeout).
      pendingEndCallBridgeRef.current = (async () => {
        if (customerSid) {
          try {
            await predictiveCtxForEnd.endCustomerLeg(customerSid);
          } catch { /* best-effort */ }
        }
        // Customer #1 is gone now. Safe to bridge the pre-dialed customer #2
        // (if one is held) into the conference. No overlap.
        try {
          const result = await predictiveCtxForEnd.handleAgentCallEnded();
          // If the bridge succeeded, mark the next lead as pre-bridged so the
          // auto-dial doesn't call them AGAIN — they're already on the line
          // via the conference.
          if (result.bridged && result.lead && result.customerCallSid) {
            preBridgedLeadIdRef.current = result.lead.id;
            preBridgedCustomerCallSidRef.current = result.customerCallSid;
          }
        } catch { /* falls back to standard auto-advance */ }
      })();
      // Clear the ref once it settles so future handleCall doesn't await stale promises.
      pendingEndCallBridgeRef.current.finally(() => {
        pendingEndCallBridgeRef.current = null;
      });

      // UI cleanup — same as the post-disconnect path below, but WITHOUT
      // touching the device call (which is the persistent conference).
      if (currentLead) {
        setCallResults((prev) => ({
          ...prev,
          [currentLead.id]: {
            ...prev[currentLead.id],
            leadId: currentLead.id,
            result: "called" as const,
            wasAnswered: false,
          },
        }));
      }
      setIsDialing(false);
      setIsCallAnswered(false);
      isCallInProgressRef.current = false;
      lastCallTimeRef.current = 0;
      dialingLeadIdRef.current = null;
      leadBeingCalledRef.current = null;
      callAttemptedForLeadRef.current = null;
      // Clear currentCallId so the next dial's customer SID is fresh.
      setCurrentCallId(null);
      if (currentLead) {
        retryOrAdvance(currentLead.id);
      } else {
        advanceToNextLead(true);
      }
      return;
    }

    if (activeCall) {
      disconnect({ userInitiated: true });

      // CRITICAL FIX: If call was in "connecting" state (no currentCallId yet because
      // Twilio "accept" event hasn't fired), auto-advance won't trigger because it needs
      // (!activeCall && currentCallId) — but currentCallId is null. Manually advance.
      if (!currentCallId) {
        // Mark current lead as "called" (was in connecting, never rang)
        if (currentLead) {
          setCallResults((prev) => ({
            ...prev,
            [currentLead.id]: {
              ...prev[currentLead.id],
              leadId: currentLead.id,
              result: "called" as const,
              wasAnswered: false,
            },
          }));
        }
        // Reset states
        setIsDialing(false);
        setIsCallAnswered(false);
        isCallInProgressRef.current = false;
        lastCallTimeRef.current = 0;
        dialingLeadIdRef.current = null;
        leadBeingCalledRef.current = null;
        // Clear the call-attempted flag so the post-disconnect useEffect
        // (line ~880) doesn't see `callEndedWithoutAccept=true` and enter its
        // own retry logic in parallel with ours — that race was advancing to
        // the next lead (useEffect's maxRetries check skipped) while our
        // retryOrAdvance was still waiting on its 1500ms timer.
        callAttemptedForLeadRef.current = null;
        // Honor retry config — retry same lead if dialAttempts > 1 and we
        // haven't exceeded max retries for this lead, else advance.
        if (currentLead) {
          retryOrAdvance(currentLead.id);
        } else {
          advanceToNextLead(true);
        }
      }
      // If currentCallId IS set, auto-advance will handle it via the useEffect
    } else if (isDialingRef.current) {
      // CRITICAL FIX: Agent hit End BEFORE Twilio SDK connected (activeCall is still null).
      // Use isDialingRef (not isDialing state) because React may not have re-rendered yet
      // after handleCall called setIsDialing(true) — the state closure would be stale.
      // handleCall() is mid-await (API call or device.connect()) — the call hasn't been
      // created yet so there's nothing to disconnect. Reset state and advance.
      // The in-flight handleCall will abort when its await resolves and sees
      // currentLeadRef changed (stale-lead check).
      isDialingRef.current = false;
      if (currentLead) {
        setCallResults((prev) => ({
          ...prev,
          [currentLead.id]: {
            ...prev[currentLead.id],
            leadId: currentLead.id,
            result: "called" as const,
            wasAnswered: false,
          },
        }));
      }
      setIsDialing(false);
      setIsCallAnswered(false);
      isCallInProgressRef.current = false;
      lastCallTimeRef.current = 0;
      dialingLeadIdRef.current = null;
      leadBeingCalledRef.current = null;
      // Same rationale as Path 1 — clear callAttemptedForLeadRef so the
      // post-disconnect useEffect doesn't enter its retry logic and race.
      callAttemptedForLeadRef.current = null;
      // Honor retry config (same logic as Path 1 above).
      if (currentLead) {
        retryOrAdvance(currentLead.id);
      } else {
        advanceToNextLead(true);
      }
    }
  }, [activeCall, disconnect, currentCallId, currentLead, advanceToNextLead, isDeviceReady]);

  const handleDispositionSave = (disposition: string, notes: string) => {
    if (!currentLead) return;

    setCallResults((prev) => ({
      ...prev,
      [currentLead.id]: {
        ...prev[currentLead.id],
        disposition,
        notes,
      },
    }));

    setShowDisposition(false);

    // Clear disposition pause state before advancing
    awaitingDispositionRef.current = false;
    setAwaitingDisposition(false);

    // Advance and auto-dial next lead (speed dial mode)
    advanceToNextLead(true);
  };

  // Skip disposition — agent doesn't want to take notes, just advance
  const handleSkipDisposition = () => {
    setShowDisposition(false);
    awaitingDispositionRef.current = false;
    setAwaitingDisposition(false);
    advanceToNextLead(true);
  };

  const handlePause = useCallback(() => {
    // Spam protection: Debounce rapid clicks
    const now = Date.now();
    if (now - lastSkipTimeRef.current < SKIP_DEBOUNCE_MS) {
      return;
    }
    lastSkipTimeRef.current = now;

    // Disconnect any active call when pausing.
    // userInitiated: true — agent paused, so toast attribution is correct.
    if (activeCall) {
      disconnect({ userInitiated: true });
    }

    // Mark that we manually paused - prevents auto-advance from happening
    manuallyAdvancedRef.current = true;

    // Update both state and ref
    setIsPaused(true);
    isPausedRef.current = true;
    setCurrentCallId(null); // Clear call ID since we're pausing
  }, [activeCall, disconnect]);

  // Resume - check if current lead was already called, if so advance to next
  const handleResume = useCallback(() => {
    // Spam protection: Debounce rapid clicks
    const now = Date.now();
    if (now - lastSkipTimeRef.current < SKIP_DEBOUNCE_MS) {
      return;
    }
    lastSkipTimeRef.current = now;

    // Reset the manual advance flag so auto-advance can work again
    manuallyAdvancedRef.current = false;

    // Update ref FIRST (synchronous) so handleCall sees the updated value immediately
    isPausedRef.current = false;
    setIsPaused(false);

    // CRITICAL: Clear dialingLeadIdRef on resume to allow the current lead to be dialed
    // Without this, if we paused mid-dial, the ref still has the old lead ID and blocks the call
    dialingLeadIdRef.current = null;

    // CRITICAL: Clear sessionStoppedRef on resume — this was the main bug causing auto-dial
    // to not work after resume. The call-end logic sets sessionStoppedRef=true when it sees
    // the next index would exceed leads.length, but on resume we need to re-evaluate.
    // If there are still leads remaining, the session is NOT stopped.
    if (currentIndexRef.current < leads.length) {
      sessionStoppedRef.current = false;
      completionFiredRef.current = false;
    }

    // Clear disposition pause if active — agent clicked Resume instead of "Next Lead"
    // after a 60s+ call. Without this, awaitingDispositionRef stays true and blocks auto-advance.
    awaitingDispositionRef.current = false;
    setAwaitingDisposition(false);

    // Clear pending retry check — prevents the auto-dial effect from being blocked by a stale
    // pendingRetryCheckRef that was set before the disposition pause triggered.
    pendingRetryCheckRef.current = false;
    pendingRetryCheckSetAtRef.current = null;

    // Restore power dialer mode — handleStopSession sets this to false to allow incoming calls.
    // Without restoring it, auto-advance and retry logic both check isInPowerDialerModeRef
    // and skip dialing when it's false, causing the "have to manually click next lead" bug.
    setIsInPowerDialerMode(true);

    // Check if the current lead was already called (has a result in callResults)
    const currentLeadWasCalled = currentLead && callResultsRef.current[currentLead.id];

    if (currentLeadWasCalled) {
      // Current lead was already dialed - advance to next lead and auto-dial
      advanceToNextLead(true);
    } else {
      // Current lead hasn't been called yet - dial it
      setTimeout(() => {
        // CRITICAL: Re-check ALL conditions including sessionStoppedRef and bounds
        if (
          currentLead &&
          isDeviceReady &&
          hasMicrophone !== false &&
          hasActiveNumbers &&
          !isDialing &&
          !activeCall &&
          !sessionStoppedRef.current &&
          currentIndexRef.current < leads.length
        ) {
          handleCall();
        }
      }, 300);
    }
  }, [currentLead, isDeviceReady, hasMicrophone, hasActiveNumbers, isDialing, activeCall, handleCall, advanceToNextLead, leads.length]);

  // Stop session - disconnect any active call first
  const handleStopSession = useCallback(() => {
    // Spam protection: Debounce rapid clicks
    const now = Date.now();
    if (now - lastSkipTimeRef.current < SKIP_DEBOUNCE_MS) {
      return;
    }
    lastSkipTimeRef.current = now;

    // CRITICAL: Mark session as stopped FIRST to prevent any pending auto-dial timers from firing
    sessionStoppedRef.current = true;
    completionFiredRef.current = true; // Prevent natural completion from double-firing
    isPausedRef.current = true;
    pendingRetryCheckRef.current = false; pendingRetryCheckSetAtRef.current = null; // Clear pending retry to prevent blocking auto-dial on resume

    // End the predictive dial session if one is active. Fire-and-forget;
    // backend will mark it as agent_stopped + clean up in-memory state.
    // We DON'T need to wait for this before stopping the call — the agent's
    // experience should be instant when they click Stop.
    if (predictiveRef.current.isPredictiveActive) {
      predictiveRef.current.endSession('agent_stopped').catch(() => { /* swallow */ });
    }

    // Disconnect any active call when stopping session.
    // userInitiated: true — agent stopped the session intentionally.
    if (activeCall) {
      disconnect({ userInitiated: true });
    }

    // Allow incoming calls again
    setIsInPowerDialerMode(false);

    // Clear all state
    setCurrentCallId(null);
    setIsPaused(true);

    // Calculate stats from callResultsRef (use ref for latest value, avoids stale closure)
    const results = Object.values(callResultsRef.current);
    const totalDialTime = getDialSeconds();
    const offset = resumeOffsetRef.current;
    const stats = {
      answered: results.filter(r => r.result === "answered" || r.wasAnswered).length,
      called: results.length + offset,
      totalDialTime,
      appointments: results.filter(r => r.leadStatus === 'Appt').length,
      underwriting: results.filter(r => r.leadStatus === 'Underwriting').length,
      sold: results.filter(r => r.leadStatus === 'Sold & Approved').length,
    };

    // Persist stop to backend (fire-and-forget)
    if (backendSessionIdRef.current) {
      dialerSessionApi
        .completeSession(backendSessionIdRef.current, {
          status: 'stopped',
          currentLeadIndex: currentIndexRef.current,
          completedCalls: results.length + offset,
          totalAnswered: stats.answered,
          totalFailed: results.filter(r => r.result === 'failed').length,
          totalVoicemail: results.filter(r => r.result === 'voicemail').length,
          totalSkipped: results.filter(r => r.result === 'skipped').length,
          totalDialTime: stats.totalDialTime,
          outcome: {
            appointments: stats.appointments,
            underwriting: stats.underwriting,
            sold: stats.sold,
          },
        })
        .catch(() => {}); // Silently ignore
    }

    // Clear persisted call results since session is ending
    if (typeof window !== 'undefined') {
      localStorage.removeItem(CALL_RESULTS_STORAGE_KEY);
    }

    onComplete({ ...stats, wasStopped: true });
  }, [activeCall, disconnect, onComplete]);

  const handleRecordingToggle = async () => {
    if (!currentCallId) {
      return;
    }

    try {
      const newState = !isCallRecording;
      // Use correct endpoint path without /api prefix
      await api.post({
        url: getApiUrl(`/communication/calls/${currentCallId}/recording`),
        body: { recording: newState },
        auth: true,
      });

      setIsCallRecording(newState);
    } catch (error) {
      // Recording toggle failed silently
    }
  };

  // Track lead status change for session stats (called when agent sets Appt, UWR, Sold, etc.)
  const trackLeadStatus = useCallback((leadId: string, status: string) => {
    // CRITICAL: Use ref as source of truth, not state, to avoid stale closure issues
    // The ref is updated synchronously in customer_answered callback and may be ahead of state
    const existingRef = callResultsRef.current[leadId] || { leadId, result: "answered" as const };
    const updatedRef: Record<string, CallResult> = {
      ...callResultsRef.current,
      [leadId]: {
        ...existingRef,
        leadStatus: status,
      },
    };
    // Update the ref immediately
    callResultsRef.current = updatedRef;

    // Also update state for UI reactivity
    setCallResults((prev) => {
      const existing = prev[leadId] || { leadId, result: "answered" as const };
      return {
        ...prev,
        [leadId]: {
          ...existing,
          leadStatus: status,
        },
      };
    });
  }, []);

  // Cleanup on unmount — clear power dialer mode so incoming calls are no longer suppressed
  useEffect(() => {
    return () => {
      setIsInPowerDialerMode(false);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty deps — only run cleanup on actual unmount

  // Count stats - answered = customer picked up, failed = bad#/DNC, skipped = user skipped
  const answeredCount = Object.values(callResults).filter(
    (result) => (result.result === "answered" || result.wasAnswered) && result.result !== "voicemail"
  ).length;
  const failedCount = Object.values(callResults).filter(
    (result) => result.result === "failed"
  ).length;
  const skippedCount = Object.values(callResults).filter(
    (result) => result.result === "skipped"
  ).length;
  const dispositionCount = Object.values(callResults).filter(
    (result) => result.disposition
  ).length;
  const voicemailCount = Object.values(callResults).filter(
    (result) => result.result === "voicemail"
  ).length;
  // Total calls made (for reference)
  const calledCount = Object.values(callResults).filter(
    (result) => result.result === "called" || result.result === "answered" || result.result === "voicemail"
  ).length;

  // External emergency-pause listener. When an incoming call arrives mid-session
  // and the agent clicks Hold & Answer / Merge on the call-waiting widget,
  // IncomingCallContext dispatches this event. Unlike the normal Pause button,
  // emergency-pause must NOT disconnect the active call — the hold/merge flow
  // needs that call alive to put on hold music or redirect into the new
  // conference. We only set the paused flags so dialNextLeads aborts and the
  // session stops auto-dialing. The call itself stays up until hold/merge
  // hands it off or the agent ends it.
  //
  // `emergencyPausedByUsRef` tracks whether THIS listener caused the pause (vs.
  // the user having manually paused earlier). Only when we caused it do we
  // auto-unpause on `emergency-resume` — otherwise a manual pause would get
  // silently overridden.
  const emergencyPausedByUsRef = useRef(false);
  // Refs so the emergency-resume handler (which has [] deps) can call the
  // latest advance function / read current lead count without stale closures.
  const advanceToNextLeadRef = useRef(advanceToNextLead);
  useEffect(() => { advanceToNextLeadRef.current = advanceToNextLead; }, [advanceToNextLead]);
  const leadsLengthRef = useRef(leads.length);
  useEffect(() => { leadsLengthRef.current = leads.length; }, [leads.length]);
  // Battle-tested handleResume path — used by emergency-resume to avoid
  // re-implementing the full state reset + dial chain.
  const handleResumeRef = useRef(handleResume);
  useEffect(() => { handleResumeRef.current = handleResume; }, [handleResume]);
  useEffect(() => {
    const onEmergencyPause = () => {
      if (isPausedRef.current) {
        // Already paused — could be our own prior emergency-pause
        // (resumeHeldCall re-dispatches as belt-and-suspenders) OR a
        // user-initiated manual pause. Either way DO NOT touch
        // `emergencyPausedByUsRef`. Overwriting it to false here was
        // the bug: our original emergency-pause set it to true, the
        // re-dispatch flipped it to false, and then emergency-resume
        // skipped clearing isPausedRef — leaving the dialer frozen
        // after Hold & Answer → end → redial observed on Caius.
        return;
      }
      emergencyPausedByUsRef.current = true;
      // IMPORTANT: do NOT set manuallyAdvancedRef here. That ref is a
      // different signal — it says "the Skip button already advanced us,
      // so the auto-advance effect should early-return". If we set it
      // during emergency-pause, the auto-advance effect at line 886
      // hits its early-return branch when the post-merge/post-resume
      // call ends and NEVER schedules the setTimeout — so we never
      // increment currentIndex and the next auto-dial re-dials the
      // same lead (Caius). isPausedRef alone is enough to block dial
      // during the incoming-call flow; manuallyAdvancedRef stays clean
      // so the natural advance logic runs when the call finally ends.
      isPausedRef.current = true;
      setIsPaused(true);
      // Deliberately do NOT call disconnect() or clear currentCallId —
      // that would kill the call the user is trying to hold/merge.
    };
    const onEmergencyResume = () => {
      // The event `powerdialer:emergency-resume` fires from EXACTLY ONE
      // place (IncomingCallContext auto-resume effect) and only when the
      // entire call-waiting cycle has ended — activeCallSid→null AND
      // heldCall=null. That means the held lead's call is done.
      //
      // Earlier attempts re-implemented the advance + auto-dial chain
      // inline, but `handleCall` kept hitting pause paths (transient
      // hasMicrophone/isDeviceReady false, stuck pendingRetryCheckRef,
      // etc.) and the UI stayed on "Paused" until the user clicked
      // Resume manually. Once clicked, it worked perfectly.
      //
      // So: just call handleResume directly. It's the exact battle-tested
      // path that works when clicked — full state reset, bounce off
      // debounce, advance if lead was called, dial if not. By the time
      // emergency-resume fires, Caius was definitely called, so
      // handleResume → advanceToNextLead → autoDialNextLead → handleCall.
      //
      // Reset skip debounce to 0 so handleResume's spam-protection at
      // line 1975 doesn't early-return (emergency-resume shouldn't be
      // debounced against user skip clicks).
      emergencyPausedByUsRef.current = false;
      lastSkipTimeRef.current = 0;
      handleResumeRef.current?.();
    };
    window.addEventListener('powerdialer:emergency-pause', onEmergencyPause);
    window.addEventListener('powerdialer:emergency-resume', onEmergencyResume);
    return () => {
      window.removeEventListener('powerdialer:emergency-pause', onEmergencyPause);
      window.removeEventListener('powerdialer:emergency-resume', onEmergencyResume);
    };
  }, []);

  // External force-end listener. Fired when the agent initiates a MANUAL
  // outbound call (from lead detail) while a power dialer session is alive.
  // Fully tears down the session — the agent has moved on from the dialer.
  // Without this the paused power-dialer widget ghosts over the manual call.
  const handleStopSessionRef2 = useRef(handleStopSession);
  useEffect(() => { handleStopSessionRef2.current = handleStopSession; }, [handleStopSession]);
  useEffect(() => {
    const onForceEnd = () => { handleStopSessionRef2.current?.(); };
    window.addEventListener('powerdialer:force-end', onForceEnd);
    return () => window.removeEventListener('powerdialer:force-end', onForceEnd);
  }, []);

  return {
    currentIndex,
    currentLead,
    isDialing,
    isPaused,
    callResults,
    showDisposition,
    formData,
    answeredCount,
    failedCount,
    skippedCount,
    dispositionCount,
    calledCount,
    voicemailCount,
    isCallAnswered,
    currentCallId,
    isCallRecording,
    isDTMFListening,
    setIsDTMFListening,
    isSpeechProcessing,
    setIsSpeechProcessing,
    hasMicrophone,
    // Speed dial state
    isWaitingForRing,
    isNavigating: isNavigatingRef.current,
    // Voicemail handling
    showVoicemailPrompt,
    setShowVoicemailPrompt,
    showVoicemailModal,
    setShowVoicemailModal,
    // Double-dial retry state
    isRetryAttempt,
    // Failure toast for UI consumption
    failureToast,
    setFailureToast,
    // Session timing
    getDialSeconds,
    updateFormData,
    handleCall,
    handleSkip,
    dropVoicemailAdvance, // Instant advance after a manual voicemail drop (no disconnect)
    handleMarkFailed,
    handleDispositionSave,
    handleSkipDisposition, // Skip disposition modal and advance immediately
    awaitingDisposition, // True when dialer is paused for disposition after 60s+ call
    handlePause, // Pause session - disconnects active call
    handleResume, // Resume and auto-dial current lead
    handleStopSession, // Stop session - disconnects active call and ends session
    handleRecordingToggle,
    trackLeadStatus, // Track lead status for session stats
    setShowDisposition,
    // Twilio Device values
    isDeviceReady,
    // In predictive mode these are gated so the UI shows "no active call"
    // between customer dials (raw activeCall is the persistent conference).
    activeCall: effectiveActiveCall,
    activeCallSid,  // For DTMF polling
    disconnect,
    handleEndCall, // End call and auto-advance to next lead
    mute,
    unmute,
    hold,
    unhold,
    isMuted,
    isOnHold,
    callStatus: callStatusOverride || callStatus, // Use voicemail override when AMD detects machine
    appleScreeningDetected,
    // Per-customer duration in predictive mode (raw callDuration ticks for
    // the entire conference session, which would lie to the agent).
    callDuration: effectiveCallDuration,
    microphoneAudioLevel,
    isHardwareMuted,
    audioInputDevices,
    audioOutputDevices,
    selectedInputDeviceId,
    selectedOutputDeviceId,
    setAudioInputDevice,
    setAudioOutputDevice,
    refreshAudioDevices,
    // Audio gain/volume controls
    microphoneGain,
    speakerVolume,
    setMicrophoneGain,
    setSpeakerVolume,
  };
}
